import React, { useState, useMemo, useEffect, useCallback } from "react";
import {
  Download,
  Layers,
  RefreshCw,
  User,
  Settings,
  ArrowRight,
  ShieldAlert,
  X,
} from "lucide-react";
import { Link } from "react-router-dom";
import api from "../services/api"; // Centralized authenticated Axios instance

const BuildersCommission = () => {
  // ─── Core Application States ──────────────────────────────────────────────────
  const [builders, setBuilders] = useState([]);
  const [activeBuilderId, setActiveBuilderId] = useState(null);
  const [commissionData, setCommissionData] = useState(null);

  const [year, setYear] = useState(new Date().getFullYear());
  const [period, setPeriod] = useState("yearly");
  const [loading, setLoading] = useState(false);
  const [initialFetchLoading, setInitialFetchLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  // Toggle state indicator for modal popup window display
  const [showModalBreakdown, setShowModalBreakdown] = useState(false);

  // ─── REFACTORED ACCOUNTING ENGINE FOR PREVIOUS SYSTEM PERIODS ─────────────────
  const getCalculatedPeriodAndYear = (type) => {
    if (!type)
      return {
        computedPeriod: "yearly",
        computedYear: new Date().getFullYear(),
      };

    const normalizedType = type.toLowerCase().replace("-", "_");
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth();

    let computedPeriod = "yearly";
    let computedYear = currentDate.getFullYear();

    if (normalizedType === "monthly") {
      if (currentMonth === 0) {
        computedPeriod = "month-12";
        computedYear = currentDate.getFullYear() - 1;
      } else {
        computedPeriod = `month-${currentMonth}`;
      }
    } else if (normalizedType === "quarterly") {
      const currentQuarter = Math.floor(currentMonth / 3) + 1;
      if (currentQuarter === 1) {
        computedPeriod = "quarter-q4";
        computedYear = currentDate.getFullYear() - 1;
      } else {
        computedPeriod = `quarter-q${currentQuarter - 1}`;
      }
    } else if (
      normalizedType === "semi_annually" ||
      normalizedType === "semi_annual" ||
      normalizedType === "semi_yearly"
    ) {
      if (currentMonth < 6) {
        computedPeriod = "h2";
        computedYear = currentDate.getFullYear() - 1;
      } else {
        computedPeriod = "h1";
      }
    }

    return { computedPeriod, computedYear };
  };

  // Helper method creating query strings parameters securely
  const buildQueryParams = (targetYear, periodValue, salesmanId) => {
    const params = {
      year: String(targetYear),
      salesmanId: String(salesmanId),
    };

    if (periodValue.startsWith("month-")) {
      params.month = periodValue.split("-")[1];
    } else if (periodValue.startsWith("quarter-")) {
      params.quarter = periodValue.split("-")[1].toUpperCase();
    } else if (periodValue === "h1" || periodValue === "h2") {
      params.half = periodValue.toUpperCase();
    } else if (periodValue === "ytd") {
      params.ytd = "true";
    } else if (periodValue === "yearly") {
      params.yearly = "true";
    }
    return params;
  };

  // ─── Unified Report Processing Request Pipeline ──────────────────────────────
  // NOTE: This is intentionally stable (empty dep array). It used to depend on
  // [activeBuilderId] and fall back to that value internally, which meant its
  // identity changed on every tab click. That, in turn, re-triggered the
  // mount effect below (which depended on fetchReport), snapping the UI back
  // to the first contractor. Requiring salesmanId as an explicit argument
  // removes the need for the closure dependency entirely.
  const fetchReport = useCallback(
    async (targetYear, periodValue, salesmanId) => {
      if (!salesmanId) return;

      setLoading(true);
      try {
        const params = buildQueryParams(targetYear, periodValue, salesmanId);
        const res = await api.get("/builders/commissions", { params });

        setCommissionData(res.data.data || res.data);
      } catch (err) {
        console.error("API Pipeline Error:", err.message);
        setCommissionData(null);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  // ─── Fetch Builders Database Profiles on Mount ──────────────────────────────
  // Runs exactly once. Previously depended on [fetchReport], whose identity
  // changed on every tab click, causing this to re-run and reset
  // activeBuilderId back to data[0].
  useEffect(() => {
    console.log("MOUNT EFFECT FIRED"); // temp debug

    const fetchBuildersList = async () => {
      try {
        const res = await api.get("/builders");
        const data = res.data.data || res.data;

        setBuilders(data);
        if (Array.isArray(data) && data.length > 0) {
          const defaultBuilder = data[0];
          setActiveBuilderId(defaultBuilder.salesmanId);

          const { computedPeriod, computedYear } = getCalculatedPeriodAndYear(
            defaultBuilder.periodType,
          );
          setPeriod(computedPeriod);
          setYear(computedYear);

          fetchReport(computedYear, computedPeriod, defaultBuilder.salesmanId);
        }
      } catch (err) {
        console.error("Initialization Error:", err.message);
      } finally {
        setInitialFetchLoading(false);
      }
    };

    fetchBuildersList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // mount-only — do not add fetchReport/activeBuilderId here

  const selectedBuilder = useMemo(() => {
    return builders.find((b) => b.salesmanId === activeBuilderId) || null;
  }, [builders, activeBuilderId]);

  // ─── SMART FILTER-PERSISTING TAB CHANGE ───
  // No longer calls fetchReport directly — the [year, period, activeBuilderId]
  // effect below is the single source of truth for fetching, so every
  // contractor click results in exactly one request instead of two
  // (which previously created a race where the older response could
  // overwrite the newer one).
  const handleTabChange = (nextBuilderProfile) => {
    console.log("TAB CLICKED ->", nextBuilderProfile.salesmanId);
    const nextId = nextBuilderProfile.salesmanId;
    setShowModalBreakdown(false);

    const nextType = nextBuilderProfile.periodType
      ? nextBuilderProfile.periodType.toLowerCase().replace("-", "_")
      : "";

    const isUniversalFilter = period === "yearly" || period === "ytd";
    const isMonthlyMatch =
      period.startsWith("month-") && nextType === "monthly";
    const isQuarterlyMatch =
      period.startsWith("quarter-") && nextType === "quarterly";
    const isSemiAnnualMatch =
      (period === "h1" || period === "h2") &&
      (nextType === "semi_annual" || nextType === "semi_annually");

    if (
      !(
        isUniversalFilter ||
        isMonthlyMatch ||
        isQuarterlyMatch ||
        isSemiAnnualMatch
      )
    ) {
      const { computedPeriod, computedYear } = getCalculatedPeriodAndYear(
        nextBuilderProfile.periodType,
      );
      setPeriod(computedPeriod);
      setYear(computedYear);
    }

    // Setting the id last (after period/year) doesn't matter for correctness —
    // React batches these — but the effect below will fire once with the
    // final year/period/activeBuilderId combination.
    setActiveBuilderId(nextId);
  };

  useEffect(() => {
    if (activeBuilderId && !initialFetchLoading) {
      fetchReport(year, period, activeBuilderId);
    }
  }, [year, period, activeBuilderId, initialFetchLoading, fetchReport]);

  const periodLabel = useMemo(() => {
    if (period === "yearly") return `Full Year ${year}`;
    if (period === "ytd") return `Year to Date (YTD) ${year}`;
    if (period === "h1") return `First Half (H1) ${year}`;
    if (period === "h2") return `Second Half (H2) ${year}`;
    if (period.startsWith("quarter-"))
      return `${period.split("-")[1].toUpperCase()} ${year}`;
    if (period.startsWith("month-")) {
      const m = Number(period.split("-")[1]);
      const months = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ];
      return `${months[m - 1]} ${year}`;
    }
    return `${year}`;
  }, [period, year]);

  // ─── ACTIVE TAB ONLY EXCEL EXPORT WORKFLOW ────────────────────────────────
  const exportAllToExcel = async () => {
    if (!commissionData || !selectedBuilder) return;
    setExporting(true);

    try {
      const ExcelJS = require("exceljs");
      const workbook = new ExcelJS.Workbook();

      const tabTitle = selectedBuilder.name
        .replace(/[\[\]\*\?\:\/\\]/g, "")
        .substring(0, 30);

      const worksheet = workbook.addWorksheet(tabTitle, {
        pageSetup: {
          orientation: "landscape",
          paperSize: 9,
          fitToPage: true,
          fitToWidth: 1,
          fitToHeight: 1,
        },
        views: [{ showGridLines: true }],
      });

      worksheet.columns = [
        { header: "Salesman ID", key: "id", width: 15 },
        { header: "Salesman Name", key: "name", width: 30 },
        { header: "Total Sales", key: "sales", width: 24 },
        { header: "Rate (%) / Amount ($)", key: "rateValue", width: 22 },
        { header: "Rate Type", key: "rateType", width: 22 },
        { header: "Commission Earned", key: "comm", width: 24 },
      ];

      const thinBorder = {
        top: { style: "thin", color: { argb: "CBD5E1" } },
        left: { style: "thin", color: { argb: "CBD5E1" } },
        bottom: { style: "thin", color: { argb: "CBD5E1" } },
        right: { style: "thin", color: { argb: "CBD5E1" } },
      };

      const headerRow = worksheet.getRow(1);
      headerRow.height = 26;
      headerRow.eachCell((cell) => {
        cell.font = {
          bold: true,
          name: "Arial",
          size: 11,
          color: { argb: "FFFFFF" },
        };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "475569" },
        };
        cell.alignment = { vertical: "middle", horizontal: "center" };
        cell.border = thinBorder;
      });

      const personalRateRaw =
        commissionData.calculationDetails?.rateApplied || "";
      let personalRateValue = personalRateRaw;
      let personalRateType = "Flat Rate";

      if (personalRateRaw.includes("(Marginal)")) {
        personalRateValue = personalRateRaw.replace(" (Marginal)", "");
        personalRateType = "Marginal progressive";
      } else if (personalRateRaw.includes("(Flat Payout)")) {
        personalRateValue = personalRateRaw.replace(" (Flat Payout)", "");
        personalRateType = "Flat Bounty Amount";
      } else if (
        commissionData.calculationDetails?.calculationType ===
        "marginal_progressive"
      ) {
        personalRateValue = "Progressive Matrix";
        personalRateType = "Marginal progressive";
      }

      const personalRow = worksheet.addRow({
        id: commissionData.employeeId,
        name: commissionData.name,
        sales: Number(commissionData.totalSales) || 0,
        rateValue: personalRateValue,
        rateType: personalRateType,
        comm:
          Number(
            commissionData.personalCommission !== undefined
              ? commissionData.personalCommission
              : commissionData.calculatedCommission,
          ) || 0,
      });
      personalRow.height = 22;

      let managerRow = null;
      if (
        commissionData.isManager &&
        commissionData.calculationDetails?.managerTeamCommissions
      ) {
        const mgrData =
          commissionData.calculationDetails.managerTeamCommissions;
        managerRow = worksheet.addRow({
          id: commissionData.employeeId,
          name: commissionData.name,
          sales: Number(mgrData.teamSalesTotal) || 0,
          rateValue: mgrData.managerTeamRate,
          rateType: "Team Override Bonus",
          comm: Number(mgrData.teamCommissionEarned) || 0,
        });
        managerRow.height = 22;
      }

      const spacerRow = worksheet.addRow({});
      spacerRow.height = 18;

      const totalSalesSum =
        Number(commissionData.totalSales) +
        Number(
          commissionData.calculationDetails?.managerTeamCommissions
            ?.teamSalesTotal || 0,
        );
      const grandTotalRow = worksheet.addRow({
        id: "",
        name: "GRAND TOTAL:",
        sales: totalSalesSum,
        rateValue: "",
        rateType: "",
        comm: Number(commissionData.calculatedCommission) || 0,
      });
      grandTotalRow.height = 24;

      const doubleBottomBorder = {
        top: { style: "thin", color: { argb: "94A3B8" } },
        left: { style: "thin", color: { argb: "CBD5E1" } },
        right: { style: "thin", color: { argb: "CBD5E1" } },
        bottom: { style: "double", color: { argb: "475569" } },
      };

      grandTotalRow.eachCell((cell, colNumber) => {
        cell.font = { bold: true, name: "Arial", size: 11 };
        cell.border = doubleBottomBorder;
        if (colNumber === 2) {
          cell.alignment = { horizontal: "right", vertical: "middle" };
        }
      });

      const ACCOUNTING_MASK =
        '_($* #,##0.00_);_($* (#,##0.00);_($* "-"??_);_(@_)';

      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber === 1) return;

        const totalDataRows = managerRow ? 3 : 2;
        if (rowNumber <= totalDataRows) {
          row.eachCell((cell) => {
            cell.border = thinBorder;
            cell.alignment = { vertical: "middle" };
          });
        }

        const idCell = row.getCell("id");
        const salesCell = row.getCell("sales");
        const rateValueCell = row.getCell("rateValue");
        const rateTypeCell = row.getCell("rateType");
        const commCell = row.getCell("comm");

        if (idCell.value)
          idCell.alignment = { horizontal: "center", vertical: "middle" };
        if (rateValueCell.value && rowNumber !== grandTotalRow.number) {
          rateValueCell.alignment = {
            horizontal: "center",
            vertical: "middle",
          };
        }
        if (rateTypeCell.value) {
          rateTypeCell.alignment = { horizontal: "center", vertical: "middle" };
        }

        if (typeof salesCell.value === "number") {
          salesCell.numFmt = ACCOUNTING_MASK;
          salesCell.alignment = { horizontal: "right", vertical: "middle" };
        }
        if (typeof commCell.value === "number") {
          commCell.numFmt = ACCOUNTING_MASK;
          commCell.alignment = { horizontal: "right", vertical: "middle" };
        }
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);

      const cleanName = commissionData.name
        .replace(/\s+/g, "_")
        .replace(/[^a-zA-Z0-9_]/g, "");
      link.download = `Commission_Report_${cleanName}_${periodLabel.replace(/\s+/g, "_")}.xlsx`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error("Excel Active Export Runtime Error:", err.message);
    } finally {
      setExporting(false);
    }
  };

  const formatPeriodLabel = (label) => {
    if (!label) return "";
    const clean = label.toLowerCase().replace("-", "_");
    if (
      clean === "semi_annual" ||
      clean === "semi_annually" ||
      clean === "semi_yearly"
    ) {
      return "Semi-Annual";
    }
    return label.charAt(0).toUpperCase() + label.slice(1);
  };

  const formatCurrency = (val) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(val || 0);
  };

  if (initialFetchLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center text-slate-400 space-y-3">
          <RefreshCw className="w-6 h-6 animate-spin mx-auto text-slate-900" />
          <p className="text-xs font-medium tracking-wide uppercase">
            Initializing System...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex text-slate-900 font-sans antialiased">
      {/* Print styles */}
      <style>{`
        @media print {
          @page { size: letter; margin: 0.5in !important; }
          html, body { background: #fff !important; color: #000 !important; height: auto !important; min-height: 0 !important; overflow: visible !important; }
          aside, .print\\:hide, button, a, input, select { display: none !important; }
          main { padding: 0 !important; margin: 0 !important; max-width: 100% !important; width: 100% !important; overflow: visible !important; }
          .grid { display: grid !important; gap: 15px !important; grid-template-columns: repeat(2, 1fr) !important; page-break-inside: avoid !important; margin-bottom: 20px !important; }
          .bg-white { border: 1px solid #cbd5e1 !important; box-shadow: none !important; background: #fff !important; page-break-inside: avoid !important; }
          tr { page-break-inside: avoid !important; }
        }
      `}</style>

      {/* ─── SIDEBAR ─── */}
      <aside className="w-64 bg-white border-r border-slate-100 flex flex-col shrink-0 sticky top-0 h-screen">
        <div className="p-5 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <div className="w-2.5 h-2.5 bg-indigo-600 rounded-full" />
            <h2 className="text-xs font-bold uppercase tracking-wider text-slate-400">
              Contractors
            </h2>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto p-3 space-y-1">
          {builders.map((builder) => (
            <button
              key={builder.salesmanId}
              onClick={() => handleTabChange(builder)}
              className={`w-full px-3 py-2.5 text-sm font-medium rounded-lg transition flex items-start gap-3 text-left ${
                activeBuilderId === builder.salesmanId
                  ? "bg-slate-900 text-white shadow-sm font-semibold"
                  : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
              }`}
            >
              {builder.isManager ? (
                <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0 text-blue-500" />
              ) : (
                <User
                  className={`w-4 h-4 mt-0.5 shrink-0 ${activeBuilderId === builder.salesmanId ? "text-indigo-400" : "text-slate-400"}`}
                />
              )}
              <div className="truncate">
                <p className="leading-tight truncate">{builder.name}</p>
                <p className="text-[10px] uppercase tracking-wider font-medium mt-0.5 text-slate-400">
                  {formatPeriodLabel(builder.periodType)}{" "}
                  {builder.isManager && "(Manager)"}
                </p>
              </div>
            </button>
          ))}
        </nav>
      </aside>

      {/* ─── MAIN CONTENT AREA ─── */}
      <main className="flex-1 p-8 space-y-6 overflow-x-hidden max-w-6xl">
        {/* Header Toolbar */}
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-200 pb-5">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900">
              Builders Commission
            </h1>
            <p className="text-slate-500 text-sm">
              {selectedBuilder?.name || "Builder Summary"} • {periodLabel}
            </p>
          </div>

          <div className="flex items-center gap-3 print:hide">
            <input
              type="number"
              min="2000"
              max="2100"
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="border border-slate-200 bg-white rounded-lg px-2.5 py-1.5 w-20 text-sm font-semibold text-center focus:outline-none focus:ring-1 focus:ring-slate-400"
            />

            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="border border-slate-200 bg-white rounded-lg px-3 py-1.5 text-sm font-medium focus:outline-none focus:ring-1 focus:ring-slate-400"
            >
              <optgroup label="Global Options">
                <option value="yearly">Full Year</option>
                <option value="ytd">YTD Accumulation</option>
              </optgroup>

              {selectedBuilder?.periodType === "monthly" && (
                <optgroup label="Months">
                  {[
                    "January",
                    "February",
                    "March",
                    "April",
                    "May",
                    "June",
                    "July",
                    "August",
                    "September",
                    "October",
                    "November",
                    "December",
                  ].map((m, i) => (
                    <option key={i} value={`month-${i + 1}`}>
                      {m}
                    </option>
                  ))}
                </optgroup>
              )}

              {selectedBuilder?.periodType === "quarterly" && (
                <optgroup label="Quarters">
                  <option value="quarter-q1">Q1 Block</option>
                  <option value="quarter-q2">Q2 Block</option>
                  <option value="quarter-q3">Q3 Block</option>
                  <option value="quarter-q4">Q4 Block</option>
                </optgroup>
              )}

              {(selectedBuilder?.periodType === "semi_annual" ||
                selectedBuilder?.periodType === "semi-annually" ||
                selectedBuilder?.periodType === "semi-annual") && (
                <optgroup label="Half-Year Cycles">
                  <option value="h1">First Half (H1)</option>
                  <option value="h2">Second Half (H2)</option>
                </optgroup>
              )}
            </select>

            <Link
              to="/builders/setting"
              className="flex items-center gap-2 px-3.5 py-2 border border-slate-200 bg-white hover:border-indigo-200 hover:bg-indigo-50/50 active:scale-[0.98] text-slate-600 hover:text-indigo-600 rounded-lg text-sm font-semibold shadow-sm transition-all duration-150 ease-in-out select-none print:hide"
              title="Builder Settings"
            >
              <Settings className="w-4 h-4 shrink-0" />
              <span>Settings</span>
            </Link>

            <button
              onClick={exportAllToExcel}
              disabled={exporting || !commissionData}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 active:scale-[0.98] disabled:bg-slate-100 disabled:text-slate-400 disabled:scale-100 text-white rounded-lg text-sm font-semibold shadow-sm transition-all duration-150 ease-in-out cursor-pointer select-none"
            >
              {exporting ? (
                <RefreshCw className="w-4 h-4 text-emerald-200 animate-spin shrink-0" />
              ) : (
                <Download className="w-4 h-4 text-white shrink-0" />
              )}
              <span>
                {exporting ? "Exporting Ledger..." : "Export Current Tab"}
              </span>
            </button>
          </div>
        </div>

        {/* Display Workspace */}
        {loading ? (
          <div className="py-24 text-center">
            <RefreshCw className="w-6 h-6 animate-spin mx-auto text-slate-300 mb-2" />
            <p className="text-slate-400 text-xs uppercase tracking-wider font-semibold">
              Calculating Data...
            </p>
          </div>
        ) : commissionData ? (
          <div className="space-y-6">
            {/* Cards */}
            <div
              className={`grid grid-cols-1 ${commissionData.isManager ? "md:grid-cols-4" : "md:grid-cols-3"} gap-6`}
            >
              <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                  Total Sales
                </p>
                <p className="text-2xl font-bold text-slate-900 tracking-tight mt-1">
                  {formatCurrency(commissionData.totalSales)}
                </p>
              </div>

              {commissionData.isManager && (
                <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                    Personal Commission
                  </p>
                  <p className="text-2xl font-bold text-slate-900 tracking-tight mt-1">
                    {formatCurrency(commissionData.personalCommission)}
                  </p>
                </div>
              )}

              {commissionData.isManager && (
                <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm flex flex-col justify-between">
                  <div>
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                      Team Commission
                    </p>
                    <p className="text-2xl font-bold text-slate-900 tracking-tight mt-1">
                      {formatCurrency(commissionData.teamCommissionEarned)}
                    </p>
                    <p className="text-[11px] text-slate-400 mt-1 font-medium">
                      Rate:{" "}
                      {commissionData.calculationDetails?.managerTeamCommissions
                        ?.managerTeamRate || "0%"}{" "}
                      • Team Total Vol (Inc. Self):{" "}
                      {formatCurrency(
                        commissionData.calculationDetails
                          ?.managerTeamCommissions?.teamSalesTotal,
                      )}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowModalBreakdown(true)}
                    className="mt-3 text-left w-max text-xs font-bold text-indigo-600 hover:text-indigo-800 bg-transparent border-none p-0 cursor-pointer outline-none transition select-none print:hide"
                  >
                    View Details &rarr;
                  </button>
                </div>
              )}

              <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                  {commissionData.isManager ? "Total Commission" : "Commission"}
                </p>
                <p className="text-2xl font-bold text-slate-900 tracking-tight mt-1">
                  {formatCurrency(commissionData.calculatedCommission)}
                </p>
              </div>
            </div>

            {/* Matrix Table */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="border-b border-slate-100 px-6 py-4">
                <h3 className="text-sm font-semibold text-slate-800">
                  Rate Allocation Matrix
                </h3>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-50/70 text-[11px] font-bold uppercase tracking-wider text-slate-400 border-b border-slate-100">
                      <th className="py-3 px-6">Tier Level</th>
                      <th className="py-3 px-6">Sales Volume Window</th>
                      <th className="py-3 px-6 text-right">Commission Rate</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-sm">
                    {selectedBuilder?.brackets?.map((bracket, index) => {
                      const isCurrent =
                        commissionData.totalSales >= bracket.from &&
                        commissionData.totalSales <= bracket.to;

                      return (
                        <tr
                          key={index}
                          className={`transition ${isCurrent ? "bg-indigo-50/30 font-medium" : "text-slate-600"}`}
                        >
                          <td className="py-4 px-6 relative">
                            {isCurrent && (
                              <div className="absolute left-0 top-0 bottom-0 w-1 bg-indigo-600" />
                            )}
                            <span
                              className={
                                isCurrent
                                  ? "text-indigo-600 font-semibold"
                                  : "text-slate-900"
                              }
                            >
                              Tier {index + 1}
                            </span>
                          </td>
                          <td className="py-4 px-6">
                            <div className="flex items-center gap-2 text-slate-500">
                              <span className="text-slate-900 font-mono">
                                {formatCurrency(bracket.from)}
                              </span>
                              <ArrowRight className="w-3.5 h-3.5 opacity-50" />
                              <span className="font-mono">
                                {bracket.to >= 999999999
                                  ? "Unlimited"
                                  : formatCurrency(bracket.to)}
                              </span>
                            </div>
                          </td>
                          <td className="py-4 px-6 text-right">
                            <span
                              className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${isCurrent ? "bg-indigo-100 text-indigo-800" : "bg-slate-100 text-slate-700"}`}
                            >
                              {Number(bracket.rate).toFixed(2)}%{" "}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* ─── MODAL BREAKDOWN OVERLAY DIALOG ─── */}
            {showModalBreakdown &&
              commissionData.isManager &&
              commissionData.calculationDetails?.managerTeamCommissions
                ?.rosterBreakdown && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm animate-fadeIn print:hide">
                  <div className="bg-white border border-slate-200 rounded-2xl shadow-xl w-full max-w-3xl overflow-hidden flex flex-col max-h-[85vh]">
                    <div className="flex justify-between items-center border-b border-slate-100 px-6 py-4 sticky top-0 bg-white z-10">
                      <h3 className="text-sm font-bold text-slate-800">
                        Team Sales Volume Breakdown — {periodLabel}
                      </h3>
                      <button
                        type="button"
                        onClick={() => setShowModalBreakdown(false)}
                        className="text-slate-400 hover:text-slate-600 p-1.5 hover:bg-slate-50 rounded-lg transition cursor-pointer border-none bg-transparent"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>

                    <div className="overflow-y-auto flex-1 p-2">
                      <table className="w-full text-left border-collapse">
                        <thead>
                          <tr className="bg-slate-50/70 text-[10px] font-bold uppercase tracking-wider text-slate-400 border-b border-slate-100">
                            <th className="py-2.5 px-4 w-28">Salesman ID</th>
                            <th className="py-2.5 px-4">
                              Roster Contractor Name
                            </th>
                            <th className="py-2.5 px-4 text-right">
                              Period Sales Volume
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 text-sm text-slate-600">
                          {commissionData.calculationDetails.managerTeamCommissions.rosterBreakdown.map(
                            (member, mIdx) => (
                              <tr
                                key={mIdx}
                                className={
                                  member.isManagerSelf
                                    ? "bg-blue-50/20 font-medium"
                                    : ""
                                }
                              >
                                <td className="py-3 px-4 font-mono font-bold text-slate-400">
                                  {member.salesmanId}
                                </td>
                                <td className="py-3 px-4 font-bold text-slate-800">
                                  {member.name}{" "}
                                  {member.isManagerSelf && (
                                    <span className="text-[9px] ml-1 px-1 py-0.2 text-blue-600 bg-blue-100 rounded font-extrabold uppercase">
                                      Self
                                    </span>
                                  )}
                                </td>
                                <td className="py-3 px-4 text-right font-mono text-slate-900 font-semibold">
                                  {formatCurrency(member.salesVolume)}
                                </td>
                              </tr>
                            ),
                          )}
                        </tbody>
                      </table>
                    </div>

                    <div className="flex justify-end px-6 py-3 border-t border-slate-100 bg-slate-50 rounded-b-2xl">
                      <button
                        type="button"
                        onClick={() => setShowModalBreakdown(false)}
                        className="px-4 py-1.5 bg-slate-900 hover:bg-slate-800 text-white rounded-lg font-bold text-xs shadow-sm transition cursor-pointer outline-none border-none"
                      >
                        Close Roster view
                      </button>
                    </div>
                  </div>
                </div>
              )}
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-xl p-16 text-center shadow-sm text-slate-400">
            <Layers className="w-6 h-6 mx-auto mb-2 opacity-40 text-slate-900" />
            <p className="text-sm font-medium text-slate-600">
              No report metrics loaded.
            </p>
            <p className="text-slate-400 text-xs mt-0.5">
              Please check parameter inputs and sync parameters.
            </p>
          </div>
        )}
      </main>
    </div>
  );
};

export default BuildersCommission;

// import React, { useState, useMemo, useEffect, useCallback } from "react";
// import {
//   Download,
//   Layers,
//   RefreshCw,
//   User,
//   Settings,
//   ArrowRight,
//   ShieldAlert,
//   X,
// } from "lucide-react";
// import { Link } from "react-router-dom";
// import api from "../services/api"; // Centralized authenticated Axios instance

// const BuildersCommission = () => {
//   // ─── Core Application States ──────────────────────────────────────────────────
//   const [builders, setBuilders] = useState([]);
//   const [activeBuilderId, setActiveBuilderId] = useState(null);
//   const [commissionData, setCommissionData] = useState(null);

//   const [year, setYear] = useState(new Date().getFullYear());
//   const [period, setPeriod] = useState("yearly");
//   const [loading, setLoading] = useState(false);
//   const [initialFetchLoading, setInitialFetchLoading] = useState(true);
//   const [exporting, setExporting] = useState(false);

//   // Toggle state indicator for modal popup window display
//   const [showModalBreakdown, setShowModalBreakdown] = useState(false);

//   // ─── REFACTORED ACCOUNTING ENGINE FOR PREVIOUS SYSTEM PERIODS ─────────────────
//   const getCalculatedPeriodAndYear = (type) => {
//     if (!type)
//       return {
//         computedPeriod: "yearly",
//         computedYear: new Date().getFullYear(),
//       };

//     const normalizedType = type.toLowerCase().replace("-", "_");
//     const currentDate = new Date();
//     const currentMonth = currentDate.getMonth();

//     let computedPeriod = "yearly";
//     let computedYear = currentDate.getFullYear();

//     if (normalizedType === "monthly") {
//       if (currentMonth === 0) {
//         computedPeriod = "month-12";
//         computedYear = currentDate.getFullYear() - 1;
//       } else {
//         computedPeriod = `month-${currentMonth}`;
//       }
//     } else if (normalizedType === "quarterly") {
//       const currentQuarter = Math.floor(currentMonth / 3) + 1;
//       if (currentQuarter === 1) {
//         computedPeriod = "quarter-q4";
//         computedYear = currentDate.getFullYear() - 1;
//       } else {
//         computedPeriod = `quarter-q${currentQuarter - 1}`;
//       }
//     } else if (
//       normalizedType === "semi_annually" ||
//       normalizedType === "semi_annual" ||
//       normalizedType === "semi_yearly"
//     ) {
//       if (currentMonth < 6) {
//         computedPeriod = "h2";
//         computedYear = currentDate.getFullYear() - 1;
//       } else {
//         computedPeriod = "h1";
//       }
//     }

//     return { computedPeriod, computedYear };
//   };

//   // Helper method creating query strings parameters securely
//   const buildQueryParams = (targetYear, periodValue, salesmanId) => {
//     const params = {
//       year: String(targetYear),
//       salesmanId: String(salesmanId),
//     };

//     if (periodValue.startsWith("month-")) {
//       params.month = periodValue.split("-")[1];
//     } else if (periodValue.startsWith("quarter-")) {
//       params.quarter = periodValue.split("-")[1].toUpperCase();
//     } else if (periodValue === "h1" || periodValue === "h2") {
//       params.half = periodValue.toUpperCase();
//     } else if (periodValue === "ytd") {
//       params.ytd = "true";
//     } else if (periodValue === "yearly") {
//       params.yearly = "true";
//     }
//     return params;
//   };

//   // ─── Unified Report Processing Request Pipeline ──────────────────────────────
//   const fetchReport = useCallback(
//     async (targetYear, periodValue, salesmanId) => {
//       const targetId = salesmanId || activeBuilderId;
//       if (!targetId) return;

//       setLoading(true);
//       try {
//         const params = buildQueryParams(targetYear, periodValue, targetId);
//         const res = await api.get("/builders/commissions", { params });

//         setCommissionData(res.data.data || res.data);
//       } catch (err) {
//         console.error("API Pipeline Error:", err.message);
//         setCommissionData(null);
//       } finally {
//         setLoading(false);
//       }
//     },
//     [activeBuilderId],
//   );

//   // ─── Fetch Builders Database Profiles on Mount ──────────────────────────────
//   useEffect(() => {
//     const fetchBuildersList = async () => {
//       try {
//         const res = await api.get("/builders");
//         const data = res.data.data || res.data;

//         setBuilders(data);
//         if (Array.isArray(data) && data.length > 0) {
//           const defaultBuilder = data[0];
//           setActiveBuilderId(defaultBuilder.salesmanId);

//           const { computedPeriod, computedYear } = getCalculatedPeriodAndYear(
//             defaultBuilder.periodType,
//           );
//           setPeriod(computedPeriod);
//           setYear(computedYear);

//           fetchReport(computedYear, computedPeriod, defaultBuilder.salesmanId);
//         }
//       } catch (err) {
//         console.error("Initialization Error:", err.message);
//       } finally {
//         setInitialFetchLoading(false);
//       }
//     };

//     fetchBuildersList();
//   }, [fetchReport]);

//   const selectedBuilder = useMemo(() => {
//     return builders.find((b) => b.salesmanId === activeBuilderId) || null;
//   }, [builders, activeBuilderId]);

//   // ─── SMART FILTER-PERSISTING TAB CHANGE ───
//   const handleTabChange = (nextBuilderProfile) => {
//     const nextId = nextBuilderProfile.salesmanId;
//     setActiveBuilderId(nextId);
//     setShowModalBreakdown(false);

//     const nextType = nextBuilderProfile.periodType
//       ? nextBuilderProfile.periodType.toLowerCase().replace("-", "_")
//       : "";

//     const isUniversalFilter = period === "yearly" || period === "ytd";
//     const isMonthlyMatch =
//       period.startsWith("month-") && nextType === "monthly";
//     const isQuarterlyMatch =
//       period.startsWith("quarter-") && nextType === "quarterly";
//     const isSemiAnnualMatch =
//       (period === "h1" || period === "h2") &&
//       (nextType === "semi_annual" || nextType === "semi_annually");

//     if (
//       isUniversalFilter ||
//       isMonthlyMatch ||
//       isQuarterlyMatch ||
//       isSemiAnnualMatch
//     ) {
//       fetchReport(year, period, nextId);
//     } else {
//       const { computedPeriod, computedYear } = getCalculatedPeriodAndYear(
//         nextBuilderProfile.periodType,
//       );
//       setPeriod(computedPeriod);
//       setYear(computedYear);
//       fetchReport(computedYear, computedPeriod, nextId);
//     }
//   };

//   useEffect(() => {
//     if (activeBuilderId && !initialFetchLoading) {
//       fetchReport(year, period, activeBuilderId);
//     }
//   }, [year, period, activeBuilderId, initialFetchLoading, fetchReport]);

//   const periodLabel = useMemo(() => {
//     if (period === "yearly") return `Full Year ${year}`;
//     if (period === "ytd") return `Year to Date (YTD) ${year}`;
//     if (period === "h1") return `First Half (H1) ${year}`;
//     if (period === "h2") return `Second Half (H2) ${year}`;
//     if (period.startsWith("quarter-"))
//       return `${period.split("-")[1].toUpperCase()} ${year}`;
//     if (period.startsWith("month-")) {
//       const m = Number(period.split("-")[1]);
//       const months = [
//         "January",
//         "February",
//         "March",
//         "April",
//         "May",
//         "June",
//         "July",
//         "August",
//         "September",
//         "October",
//         "November",
//         "December",
//       ];
//       return `${months[m - 1]} ${year}`;
//     }
//     return `${year}`;
//   }, [period, year]);

//   // ─── ACTIVE TAB ONLY EXCEL EXPORT WORKFLOW ────────────────────────────────
//   const exportAllToExcel = async () => {
//     if (!commissionData || !selectedBuilder) return;
//     setExporting(true);

//     try {
//       const ExcelJS = require("exceljs");
//       const workbook = new ExcelJS.Workbook();

//       const tabTitle = selectedBuilder.name
//         .replace(/[\[\]\*\?\:\/\\]/g, "")
//         .substring(0, 30);

//       const worksheet = workbook.addWorksheet(tabTitle, {
//         pageSetup: {
//           orientation: "landscape",
//           paperSize: 9,
//           fitToPage: true,
//           fitToWidth: 1,
//           fitToHeight: 1,
//         },
//         views: [{ showGridLines: true }],
//       });

//       worksheet.columns = [
//         { header: "Salesman ID", key: "id", width: 15 },
//         { header: "Salesman Name", key: "name", width: 30 },
//         { header: "Total Sales", key: "sales", width: 24 },
//         { header: "Rate (%) / Amount ($)", key: "rateValue", width: 22 },
//         { header: "Rate Type", key: "rateType", width: 22 },
//         { header: "Commission Earned", key: "comm", width: 24 },
//       ];

//       const thinBorder = {
//         top: { style: "thin", color: { argb: "CBD5E1" } },
//         left: { style: "thin", color: { argb: "CBD5E1" } },
//         bottom: { style: "thin", color: { argb: "CBD5E1" } },
//         right: { style: "thin", color: { argb: "CBD5E1" } },
//       };

//       const headerRow = worksheet.getRow(1);
//       headerRow.height = 26;
//       headerRow.eachCell((cell) => {
//         cell.font = {
//           bold: true,
//           name: "Arial",
//           size: 11,
//           color: { argb: "FFFFFF" },
//         };
//         cell.fill = {
//           type: "pattern",
//           pattern: "solid",
//           fgColor: { argb: "475569" },
//         };
//         cell.alignment = { vertical: "middle", horizontal: "center" };
//         cell.border = thinBorder;
//       });

//       const personalRateRaw =
//         commissionData.calculationDetails?.rateApplied || "";
//       let personalRateValue = personalRateRaw;
//       let personalRateType = "Flat Rate";

//       if (personalRateRaw.includes("(Marginal)")) {
//         personalRateValue = personalRateRaw.replace(" (Marginal)", "");
//         personalRateType = "Marginal progressive";
//       } else if (personalRateRaw.includes("(Flat Payout)")) {
//         personalRateValue = personalRateRaw.replace(" (Flat Payout)", "");
//         personalRateType = "Flat Bounty Amount";
//       } else if (
//         commissionData.calculationDetails?.calculationType ===
//         "marginal_progressive"
//       ) {
//         personalRateValue = "Progressive Matrix";
//         personalRateType = "Marginal progressive";
//       }

//       const personalRow = worksheet.addRow({
//         id: commissionData.employeeId,
//         name: commissionData.name,
//         sales: Number(commissionData.totalSales) || 0,
//         rateValue: personalRateValue,
//         rateType: personalRateType,
//         comm:
//           Number(
//             commissionData.personalCommission !== undefined
//               ? commissionData.personalCommission
//               : commissionData.calculatedCommission,
//           ) || 0,
//       });
//       personalRow.height = 22;

//       let managerRow = null;
//       if (
//         commissionData.isManager &&
//         commissionData.calculationDetails?.managerTeamCommissions
//       ) {
//         const mgrData =
//           commissionData.calculationDetails.managerTeamCommissions;
//         managerRow = worksheet.addRow({
//           id: commissionData.employeeId,
//           name: commissionData.name,
//           sales: Number(mgrData.teamSalesTotal) || 0,
//           rateValue: mgrData.managerTeamRate,
//           rateType: "Team Override Bonus",
//           comm: Number(mgrData.teamCommissionEarned) || 0,
//         });
//         managerRow.height = 22;
//       }

//       const spacerRow = worksheet.addRow({});
//       spacerRow.height = 18;

//       const totalSalesSum =
//         Number(commissionData.totalSales) +
//         Number(
//           commissionData.calculationDetails?.managerTeamCommissions
//             ?.teamSalesTotal || 0,
//         );
//       const grandTotalRow = worksheet.addRow({
//         id: "",
//         name: "GRAND TOTAL:",
//         sales: totalSalesSum,
//         rateValue: "",
//         rateType: "",
//         comm: Number(commissionData.calculatedCommission) || 0,
//       });
//       grandTotalRow.height = 24;

//       const doubleBottomBorder = {
//         top: { style: "thin", color: { argb: "94A3B8" } },
//         left: { style: "thin", color: { argb: "CBD5E1" } },
//         right: { style: "thin", color: { argb: "CBD5E1" } },
//         bottom: { style: "double", color: { argb: "475569" } },
//       };

//       grandTotalRow.eachCell((cell, colNumber) => {
//         cell.font = { bold: true, name: "Arial", size: 11 };
//         cell.border = doubleBottomBorder;
//         if (colNumber === 2) {
//           cell.alignment = { horizontal: "right", vertical: "middle" };
//         }
//       });

//       const ACCOUNTING_MASK =
//         '_($* #,##0.00_);_($* (#,##0.00);_($* "-"??_);_(@_)';

//       worksheet.eachRow((row, rowNumber) => {
//         if (rowNumber === 1) return;

//         const totalDataRows = managerRow ? 3 : 2;
//         if (rowNumber <= totalDataRows) {
//           row.eachCell((cell) => {
//             cell.border = thinBorder;
//             cell.alignment = { vertical: "middle" };
//           });
//         }

//         const idCell = row.getCell("id");
//         const salesCell = row.getCell("sales");
//         const rateValueCell = row.getCell("rateValue");
//         const rateTypeCell = row.getCell("rateType");
//         const commCell = row.getCell("comm");

//         if (idCell.value)
//           idCell.alignment = { horizontal: "center", vertical: "middle" };
//         if (rateValueCell.value && rowNumber !== grandTotalRow.number) {
//           rateValueCell.alignment = {
//             horizontal: "center",
//             vertical: "middle",
//           };
//         }
//         if (rateTypeCell.value) {
//           rateTypeCell.alignment = { horizontal: "center", vertical: "middle" };
//         }

//         if (typeof salesCell.value === "number") {
//           salesCell.numFmt = ACCOUNTING_MASK;
//           salesCell.alignment = { horizontal: "right", vertical: "middle" };
//         }
//         if (typeof commCell.value === "number") {
//           commCell.numFmt = ACCOUNTING_MASK;
//           commCell.alignment = { horizontal: "right", vertical: "middle" };
//         }
//       });

//       const buffer = await workbook.xlsx.writeBuffer();
//       const blob = new Blob([buffer], {
//         type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
//       });
//       const link = document.createElement("a");
//       link.href = URL.createObjectURL(blob);

//       const cleanName = commissionData.name
//         .replace(/\s+/g, "_")
//         .replace(/[^a-zA-Z0-9_]/g, "");
//       link.download = `Commission_Report_${cleanName}_${periodLabel.replace(/\s+/g, "_")}.xlsx`;

//       document.body.appendChild(link);
//       link.click();
//       document.body.removeChild(link);
//     } catch (err) {
//       console.error("Excel Active Export Runtime Error:", err.message);
//     } finally {
//       setExporting(false);
//     }
//   };

//   const formatPeriodLabel = (label) => {
//     if (!label) return "";
//     const clean = label.toLowerCase().replace("-", "_");
//     if (
//       clean === "semi_annual" ||
//       clean === "semi_annually" ||
//       clean === "semi_yearly"
//     ) {
//       return "Semi-Annual";
//     }
//     return label.charAt(0).toUpperCase() + label.slice(1);
//   };

//   const formatCurrency = (val) => {
//     return new Intl.NumberFormat("en-US", {
//       style: "currency",
//       currency: "USD",
//       minimumFractionDigits: 2,
//       maximumFractionDigits: 2,
//     }).format(val || 0);
//   };

//   if (initialFetchLoading) {
//     return (
//       <div className="min-h-screen flex items-center justify-center bg-slate-50">
//         <div className="text-center text-slate-400 space-y-3">
//           <RefreshCw className="w-6 h-6 animate-spin mx-auto text-slate-900" />
//           <p className="text-xs font-medium tracking-wide uppercase">
//             Initializing System...
//           </p>
//         </div>
//       </div>
//     );
//   }

//   return (
//     <div className="min-h-screen bg-slate-50 flex text-slate-900 font-sans antialiased">
//       {/* Print styles */}
//       <style>{`
//         @media print {
//           @page { size: letter; margin: 0.5in !important; }
//           html, body { background: #fff !important; color: #000 !important; height: auto !important; min-height: 0 !important; overflow: visible !important; }
//           aside, .print\\:hide, button, a, input, select { display: none !important; }
//           main { padding: 0 !important; margin: 0 !important; max-width: 100% !important; width: 100% !important; overflow: visible !important; }
//           .grid { display: grid !important; gap: 15px !important; grid-template-columns: repeat(2, 1fr) !important; page-break-inside: avoid !important; margin-bottom: 20px !important; }
//           .bg-white { border: 1px solid #cbd5e1 !important; box-shadow: none !important; background: #fff !important; page-break-inside: avoid !important; }
//           tr { page-break-inside: avoid !important; }
//         }
//       `}</style>

//       {/* ─── SIDEBAR ─── */}
//       <aside className="w-64 bg-white border-r border-slate-100 flex flex-col shrink-0 sticky top-0 h-screen">
//         <div className="p-5 border-b border-slate-100">
//           <div className="flex items-center gap-2">
//             <div className="w-2.5 h-2.5 bg-indigo-600 rounded-full" />
//             <h2 className="text-xs font-bold uppercase tracking-wider text-slate-400">
//               Contractors
//             </h2>
//           </div>
//         </div>

//         <nav className="flex-1 overflow-y-auto p-3 space-y-1">
//           {builders.map((builder) => (
//             <button
//               key={builder.salesmanId}
//               onClick={() => handleTabChange(builder)}
//               className={`w-full px-3 py-2.5 text-sm font-medium rounded-lg transition flex items-start gap-3 text-left ${
//                 activeBuilderId === builder.salesmanId
//                   ? "bg-slate-900 text-white shadow-sm font-semibold"
//                   : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
//               }`}
//             >
//               {builder.isManager ? (
//                 <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0 text-blue-500" />
//               ) : (
//                 <User
//                   className={`w-4 h-4 mt-0.5 shrink-0 ${activeBuilderId === builder.salesmanId ? "text-indigo-400" : "text-slate-400"}`}
//                 />
//               )}
//               <div className="truncate">
//                 <p className="leading-tight truncate">{builder.name}</p>
//                 <p className="text-[10px] uppercase tracking-wider font-medium mt-0.5 text-slate-400">
//                   {formatPeriodLabel(builder.periodType)}{" "}
//                   {builder.isManager && "(Manager)"}
//                 </p>
//               </div>
//             </button>
//           ))}
//         </nav>
//       </aside>

//       {/* ─── MAIN CONTENT AREA ─── */}
//       <main className="flex-1 p-8 space-y-6 overflow-x-hidden max-w-6xl">
//         {/* Header Toolbar */}
//         <div className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-200 pb-5">
//           <div>
//             <h1 className="text-2xl font-bold tracking-tight text-slate-900">
//               Builders Commission
//             </h1>
//             <p className="text-slate-500 text-sm">
//               {selectedBuilder?.name || "Builder Summary"} • {periodLabel}
//             </p>
//           </div>

//           <div className="flex items-center gap-3 print:hide">
//             <input
//               type="number"
//               min="2000"
//               max="2100"
//               value={year}
//               onChange={(e) => setYear(Number(e.target.value))}
//               className="border border-slate-200 bg-white rounded-lg px-2.5 py-1.5 w-20 text-sm font-semibold text-center focus:outline-none focus:ring-1 focus:ring-slate-400"
//             />

//             <select
//               value={period}
//               onChange={(e) => setPeriod(e.target.value)}
//               className="border border-slate-200 bg-white rounded-lg px-3 py-1.5 text-sm font-medium focus:outline-none focus:ring-1 focus:ring-slate-400"
//             >
//               <optgroup label="Global Options">
//                 <option value="yearly">Full Year</option>
//                 <option value="ytd">YTD Accumulation</option>
//               </optgroup>

//               {selectedBuilder?.periodType === "monthly" && (
//                 <optgroup label="Months">
//                   {[
//                     "January",
//                     "February",
//                     "March",
//                     "April",
//                     "May",
//                     "June",
//                     "July",
//                     "August",
//                     "September",
//                     "October",
//                     "November",
//                     "December",
//                   ].map((m, i) => (
//                     <option key={i} value={`month-${i + 1}`}>
//                       {m}
//                     </option>
//                   ))}
//                 </optgroup>
//               )}

//               {selectedBuilder?.periodType === "quarterly" && (
//                 <optgroup label="Quarters">
//                   <option value="quarter-q1">Q1 Block</option>
//                   <option value="quarter-q2">Q2 Block</option>
//                   <option value="quarter-q3">Q3 Block</option>
//                   <option value="quarter-q4">Q4 Block</option>
//                 </optgroup>
//               )}

//               {(selectedBuilder?.periodType === "semi_annual" ||
//                 selectedBuilder?.periodType === "semi-annually" ||
//                 selectedBuilder?.periodType === "semi-annual") && (
//                 <optgroup label="Half-Year Cycles">
//                   <option value="h1">First Half (H1)</option>
//                   <option value="h2">Second Half (H2)</option>
//                 </optgroup>
//               )}
//             </select>

//             <Link
//               to="/builders/setting"
//               className="flex items-center gap-2 px-3.5 py-2 border border-slate-200 bg-white hover:border-indigo-200 hover:bg-indigo-50/50 active:scale-[0.98] text-slate-600 hover:text-indigo-600 rounded-lg text-sm font-semibold shadow-sm transition-all duration-150 ease-in-out select-none print:hide"
//               title="Builder Settings"
//             >
//               <Settings className="w-4 h-4 shrink-0" />
//               <span>Settings</span>
//             </Link>

//             <button
//               onClick={exportAllToExcel}
//               disabled={exporting || !commissionData}
//               className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 active:scale-[0.98] disabled:bg-slate-100 disabled:text-slate-400 disabled:scale-100 text-white rounded-lg text-sm font-semibold shadow-sm transition-all duration-150 ease-in-out cursor-pointer select-none"
//             >
//               {exporting ? (
//                 <RefreshCw className="w-4 h-4 text-emerald-200 animate-spin shrink-0" />
//               ) : (
//                 <Download className="w-4 h-4 text-white shrink-0" />
//               )}
//               <span>
//                 {exporting ? "Exporting Ledger..." : "Export Current Tab"}
//               </span>
//             </button>
//           </div>
//         </div>

//         {/* Display Workspace */}
//         {loading ? (
//           <div className="py-24 text-center">
//             <RefreshCw className="w-6 h-6 animate-spin mx-auto text-slate-300 mb-2" />
//             <p className="text-slate-400 text-xs uppercase tracking-wider font-semibold">
//               Calculating Data...
//             </p>
//           </div>
//         ) : commissionData ? (
//           <div className="space-y-6">
//             {/* Cards */}
//             <div
//               className={`grid grid-cols-1 ${commissionData.isManager ? "md:grid-cols-4" : "md:grid-cols-3"} gap-6`}
//             >
//               <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
//                 <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
//                   Total Sales
//                 </p>
//                 <p className="text-2xl font-bold text-slate-900 tracking-tight mt-1">
//                   {formatCurrency(commissionData.totalSales)}
//                 </p>
//               </div>

//               {commissionData.isManager && (
//                 <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
//                   <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
//                     Personal Commission
//                   </p>
//                   <p className="text-2xl font-bold text-slate-900 tracking-tight mt-1">
//                     {formatCurrency(commissionData.personalCommission)}
//                   </p>
//                 </div>
//               )}

//               {commissionData.isManager && (
//                 <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm flex flex-col justify-between">
//                   <div>
//                     <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
//                       Team Commission
//                     </p>
//                     <p className="text-2xl font-bold text-slate-900 tracking-tight mt-1">
//                       {formatCurrency(commissionData.teamCommissionEarned)}
//                     </p>
//                     <p className="text-[11px] text-slate-400 mt-1 font-medium">
//                       Rate:{" "}
//                       {commissionData.calculationDetails?.managerTeamCommissions
//                         ?.managerTeamRate || "0%"}{" "}
//                       • Team Total Vol (Inc. Self):{" "}
//                       {formatCurrency(
//                         commissionData.calculationDetails
//                           ?.managerTeamCommissions?.teamSalesTotal,
//                       )}
//                     </p>
//                   </div>
//                   <button
//                     type="button"
//                     onClick={() => setShowModalBreakdown(true)}
//                     className="mt-3 text-left w-max text-xs font-bold text-indigo-600 hover:text-indigo-800 bg-transparent border-none p-0 cursor-pointer outline-none transition select-none print:hide"
//                   >
//                     View Details &rarr;
//                   </button>
//                 </div>
//               )}

//               <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
//                 <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
//                   {commissionData.isManager ? "Total Commission" : "Commission"}
//                 </p>
//                 <p className="text-2xl font-bold text-slate-900 tracking-tight mt-1">
//                   {formatCurrency(commissionData.calculatedCommission)}
//                 </p>
//               </div>
//             </div>

//             {/* Matrix Table */}
//             <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
//               <div className="border-b border-slate-100 px-6 py-4">
//                 <h3 className="text-sm font-semibold text-slate-800">
//                   Rate Allocation Matrix
//                 </h3>
//               </div>

//               <div className="overflow-x-auto">
//                 <table className="w-full text-left border-collapse">
//                   <thead>
//                     <tr className="bg-slate-50/70 text-[11px] font-bold uppercase tracking-wider text-slate-400 border-b border-slate-100">
//                       <th className="py-3 px-6">Tier Level</th>
//                       <th className="py-3 px-6">Sales Volume Window</th>
//                       <th className="py-3 px-6 text-right">Commission Rate</th>
//                     </tr>
//                   </thead>
//                   <tbody className="divide-y divide-slate-100 text-sm">
//                     {selectedBuilder?.brackets?.map((bracket, index) => {
//                       const isCurrent =
//                         commissionData.totalSales >= bracket.from &&
//                         commissionData.totalSales <= bracket.to;

//                       return (
//                         <tr
//                           key={index}
//                           className={`transition ${isCurrent ? "bg-indigo-50/30 font-medium" : "text-slate-600"}`}
//                         >
//                           <td className="py-4 px-6 relative">
//                             {isCurrent && (
//                               <div className="absolute left-0 top-0 bottom-0 w-1 bg-indigo-600" />
//                             )}
//                             <span
//                               className={
//                                 isCurrent
//                                   ? "text-indigo-600 font-semibold"
//                                   : "text-slate-900"
//                               }
//                             >
//                               Tier {index + 1}
//                             </span>
//                           </td>
//                           <td className="py-4 px-6">
//                             <div className="flex items-center gap-2 text-slate-500">
//                               <span className="text-slate-900 font-mono">
//                                 {formatCurrency(bracket.from)}
//                               </span>
//                               <ArrowRight className="w-3.5 h-3.5 opacity-50" />
//                               <span className="font-mono">
//                                 {bracket.to >= 999999999
//                                   ? "Unlimited"
//                                   : formatCurrency(bracket.to)}
//                               </span>
//                             </div>
//                           </td>
//                           <td className="py-4 px-6 text-right">
//                             <span
//                               className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${isCurrent ? "bg-indigo-100 text-indigo-800" : "bg-slate-100 text-slate-700"}`}
//                             >
//                               {Number(bracket.rate).toFixed(2)}%{" "}
//                             </span>
//                           </td>
//                         </tr>
//                       );
//                     })}
//                   </tbody>
//                 </table>
//               </div>
//             </div>

//             {/* ─── MODAL BREAKDOWN OVERLAY DIALOG ─── */}
//             {showModalBreakdown &&
//               commissionData.isManager &&
//               commissionData.calculationDetails?.managerTeamCommissions
//                 ?.rosterBreakdown && (
//                 <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm animate-fadeIn print:hide">
//                   <div className="bg-white border border-slate-200 rounded-2xl shadow-xl w-full max-w-3xl overflow-hidden flex flex-col max-h-[85vh]">
//                     <div className="flex justify-between items-center border-b border-slate-100 px-6 py-4 sticky top-0 bg-white z-10">
//                       <h3 className="text-sm font-bold text-slate-800">
//                         Team Sales Volume Breakdown — {periodLabel}
//                       </h3>
//                       <button
//                         type="button"
//                         onClick={() => setShowModalBreakdown(false)}
//                         className="text-slate-400 hover:text-slate-600 p-1.5 hover:bg-slate-50 rounded-lg transition cursor-pointer border-none bg-transparent"
//                       >
//                         <X className="w-4 h-4" />
//                       </button>
//                     </div>

//                     <div className="overflow-y-auto flex-1 p-2">
//                       <table className="w-full text-left border-collapse">
//                         <thead>
//                           <tr className="bg-slate-50/70 text-[10px] font-bold uppercase tracking-wider text-slate-400 border-b border-slate-100">
//                             <th className="py-2.5 px-4 w-28">Salesman ID</th>
//                             <th className="py-2.5 px-4">
//                               Roster Contractor Name
//                             </th>
//                             <th className="py-2.5 px-4 text-right">
//                               Period Sales Volume
//                             </th>
//                           </tr>
//                         </thead>
//                         <tbody className="divide-y divide-slate-100 text-sm text-slate-600">
//                           {commissionData.calculationDetails.managerTeamCommissions.rosterBreakdown.map(
//                             (member, mIdx) => (
//                               <tr
//                                 key={mIdx}
//                                 className={
//                                   member.isManagerSelf
//                                     ? "bg-blue-50/20 font-medium"
//                                     : ""
//                                 }
//                               >
//                                 <td className="py-3 px-4 font-mono font-bold text-slate-400">
//                                   {member.salesmanId}
//                                 </td>
//                                 <td className="py-3 px-4 font-bold text-slate-800">
//                                   {member.name}{" "}
//                                   {member.isManagerSelf && (
//                                     <span className="text-[9px] ml-1 px-1 py-0.2 text-blue-600 bg-blue-100 rounded font-extrabold uppercase">
//                                       Self
//                                     </span>
//                                   )}
//                                 </td>
//                                 <td className="py-3 px-4 text-right font-mono text-slate-900 font-semibold">
//                                   {formatCurrency(member.salesVolume)}
//                                 </td>
//                               </tr>
//                             ),
//                           )}
//                         </tbody>
//                       </table>
//                     </div>

//                     <div className="flex justify-end px-6 py-3 border-t border-slate-100 bg-slate-50 rounded-b-2xl">
//                       <button
//                         type="button"
//                         onClick={() => setShowModalBreakdown(false)}
//                         className="px-4 py-1.5 bg-slate-900 hover:bg-slate-800 text-white rounded-lg font-bold text-xs shadow-sm transition cursor-pointer outline-none border-none"
//                       >
//                         Close Roster view
//                       </button>
//                     </div>
//                   </div>
//                 </div>
//               )}
//           </div>
//         ) : (
//           <div className="bg-white border border-slate-200 rounded-xl p-16 text-center shadow-sm text-slate-400">
//             <Layers className="w-6 h-6 mx-auto mb-2 opacity-40 text-slate-900" />
//             <p className="text-sm font-medium text-slate-600">
//               No report metrics loaded.
//             </p>
//             <p className="text-slate-400 text-xs mt-0.5">
//               Please check parameter inputs and sync parameters.
//             </p>
//           </div>
//         )}
//       </main>
//     </div>
//   );
// };

// export default BuildersCommission;

// import React, { useState, useMemo, useEffect, useCallback } from "react";
// import {
//   Download,
//   Layers,
//   RefreshCw,
//   User,
//   Settings,
//   ArrowRight,
//   ShieldAlert,
//   X,
// } from "lucide-react";
// import { Link } from "react-router-dom";
// import api from "../services/api"; // Centralized authenticated Axios instance

// const BuildersCommission = () => {
//   // ─── Core Application States ──────────────────────────────────────────────────
//   const [builders, setBuilders] = useState([]);
//   const [activeBuilderId, setActiveBuilderId] = useState(null);
//   const [commissionData, setCommissionData] = useState(null);

//   const [year, setYear] = useState(new Date().getFullYear());
//   const [period, setPeriod] = useState("yearly");
//   const [loading, setLoading] = useState(false);
//   const [initialFetchLoading, setInitialFetchLoading] = useState(true);
//   const [exporting, setExporting] = useState(false);

//   // Toggle state indicator for modal popup window display
//   const [showModalBreakdown, setShowModalBreakdown] = useState(false);

//   // ─── REFACTORED ACCOUNTING ENGINE FOR PREVIOUS SYSTEM PERIODS ─────────────────
//   const getCalculatedPeriodAndYear = (type) => {
//     if (!type)
//       return {
//         computedPeriod: "yearly",
//         computedYear: new Date().getFullYear(),
//       };

//     const normalizedType = type.toLowerCase().replace("-", "_");
//     const currentDate = new Date();
//     const currentMonth = currentDate.getMonth();

//     let computedPeriod = "yearly";
//     let computedYear = currentDate.getFullYear();

//     if (normalizedType === "monthly") {
//       if (currentMonth === 0) {
//         computedPeriod = "month-12";
//         computedYear = currentDate.getFullYear() - 1;
//       } else {
//         computedPeriod = `month-${currentMonth}`;
//       }
//     } else if (normalizedType === "quarterly") {
//       const currentQuarter = Math.floor(currentMonth / 3) + 1;
//       if (currentQuarter === 1) {
//         computedPeriod = "quarter-q4";
//         computedYear = currentDate.getFullYear() - 1;
//       } else {
//         computedPeriod = `quarter-q${currentQuarter - 1}`;
//       }
//     } else if (
//       normalizedType === "semi_annually" ||
//       normalizedType === "semi_annual" ||
//       normalizedType === "semi_yearly"
//     ) {
//       if (currentMonth < 6) {
//         computedPeriod = "h2";
//         computedYear = currentDate.getFullYear() - 1;
//       } else {
//         computedPeriod = "h1";
//       }
//     }

//     return { computedPeriod, computedYear };
//   };

//   // Helper method creating query strings parameters securely
//   const buildQueryParams = (targetYear, periodValue, salesmanId) => {
//     const params = {
//       year: String(targetYear),
//       salesmanId: String(salesmanId),
//     };

//     if (periodValue.startsWith("month-")) {
//       params.month = periodValue.split("-")[1];
//     } else if (periodValue.startsWith("quarter-")) {
//       params.quarter = periodValue.split("-")[1].toUpperCase();
//     } else if (periodValue === "h1" || periodValue === "h2") {
//       params.half = periodValue.toUpperCase();
//     } else if (periodValue === "ytd") {
//       params.ytd = "true";
//     } else if (periodValue === "yearly") {
//       params.yearly = "true";
//     }
//     return params;
//   };

//   // ─── Unified Report Processing Request Pipeline ──────────────────────────────
//   const fetchReport = useCallback(
//     async (targetYear, periodValue, salesmanId) => {
//       const targetId = salesmanId || activeBuilderId;
//       if (!targetId) return;

//       setLoading(true);
//       try {
//         const params = buildQueryParams(targetYear, periodValue, targetId);
//         const res = await api.get("/builders/commissions", { params });

//         setCommissionData(res.data.data || res.data);
//       } catch (err) {
//         console.error("API Pipeline Error:", err.message);
//         setCommissionData(null);
//       } finally {
//         setLoading(false);
//       }
//     },
//     [activeBuilderId],
//   );

//   // ─── Fetch Builders Database Profiles on Mount ──────────────────────────────
//   useEffect(() => {
//     const fetchBuildersList = async () => {
//       try {
//         const res = await api.get("/builders");
//         const data = res.data.data || res.data;

//         setBuilders(data);
//         if (Array.isArray(data) && data.length > 0) {
//           const defaultBuilder = data[0];
//           setActiveBuilderId(defaultBuilder.salesmanId);

//           const { computedPeriod, computedYear } = getCalculatedPeriodAndYear(
//             defaultBuilder.periodType,
//           );
//           setPeriod(computedPeriod);
//           setYear(computedYear);

//           fetchReport(computedYear, computedPeriod, defaultBuilder.salesmanId);
//         }
//       } catch (err) {
//         console.error("Initialization Error:", err.message);
//       } finally {
//         setInitialFetchLoading(false);
//       }
//     };

//     fetchBuildersList();
//   }, [fetchReport]);

//   const selectedBuilder = useMemo(() => {
//     return builders.find((b) => b.salesmanId === activeBuilderId) || null;
//   }, [builders, activeBuilderId]);

//   // ─── SMART FILTER-PERSISTING TAB CHANGE ───
//   const handleTabChange = (nextBuilderProfile) => {
//     const nextId = nextBuilderProfile.salesmanId;
//     setActiveBuilderId(nextId);
//     setShowModalBreakdown(false);

//     const nextType = nextBuilderProfile.periodType
//       ? nextBuilderProfile.periodType.toLowerCase().replace("-", "_")
//       : "";

//     const isUniversalFilter = period === "yearly" || period === "ytd";
//     const isMonthlyMatch =
//       period.startsWith("month-") && nextType === "monthly";
//     const isQuarterlyMatch =
//       period.startsWith("quarter-") && nextType === "quarterly";
//     const isSemiAnnualMatch =
//       (period === "h1" || period === "h2") &&
//       (nextType === "semi_annual" || nextType === "semi_annually");

//     if (
//       isUniversalFilter ||
//       isMonthlyMatch ||
//       isQuarterlyMatch ||
//       isSemiAnnualMatch
//     ) {
//       fetchReport(year, period, nextId);
//     } else {
//       const { computedPeriod, computedYear } = getCalculatedPeriodAndYear(
//         nextBuilderProfile.periodType,
//       );
//       setPeriod(computedPeriod);
//       setYear(computedYear);
//       fetchReport(computedYear, computedPeriod, nextId);
//     }
//   };

//   useEffect(() => {
//     if (activeBuilderId && !initialFetchLoading) {
//       fetchReport(year, period, activeBuilderId);
//     }
//   }, [year, period, activeBuilderId, initialFetchLoading, fetchReport]);

//   const periodLabel = useMemo(() => {
//     if (period === "yearly") return `Full Year ${year}`;
//     if (period === "ytd") return `Year to Date (YTD) ${year}`;
//     if (period === "h1") return `First Half (H1) ${year}`;
//     if (period === "h2") return `Second Half (H2) ${year}`;
//     if (period.startsWith("quarter-"))
//       return `${period.split("-")[1].toUpperCase()} ${year}`;
//     if (period.startsWith("month-")) {
//       const m = Number(period.split("-")[1]);
//       const months = [
//         "January",
//         "February",
//         "March",
//         "April",
//         "May",
//         "June",
//         "July",
//         "August",
//         "September",
//         "October",
//         "November",
//         "December",
//       ];
//       return `${months[m - 1]} ${year}`;
//     }
//     return `${year}`;
//   }, [period, year]);

//   // ─── ACTIVE TAB ONLY EXCEL EXPORT WORKFLOW ────────────────────────────────
//   const exportAllToExcel = async () => {
//     if (!commissionData || !selectedBuilder) return;
//     setExporting(true);

//     try {
//       const ExcelJS = require("exceljs");
//       const workbook = new ExcelJS.Workbook();

//       const tabTitle = selectedBuilder.name
//         .replace(/[\[\]\*\?\:\/\\]/g, "")
//         .substring(0, 30);

//       const worksheet = workbook.addWorksheet(tabTitle, {
//         pageSetup: {
//           orientation: "landscape",
//           paperSize: 9,
//           fitToPage: true,
//           fitToWidth: 1,
//           fitToHeight: 1,
//         },
//         views: [{ showGridLines: true }],
//       });

//       worksheet.columns = [
//         { header: "Salesman ID", key: "id", width: 15 },
//         { header: "Salesman Name", key: "name", width: 30 },
//         { header: "Total Sales", key: "sales", width: 24 },
//         { header: "Rate (%) / Amount ($)", key: "rateValue", width: 22 },
//         { header: "Rate Type", key: "rateType", width: 22 },
//         { header: "Commission Earned", key: "comm", width: 24 },
//       ];

//       const thinBorder = {
//         top: { style: "thin", color: { argb: "CBD5E1" } },
//         left: { style: "thin", color: { argb: "CBD5E1" } },
//         bottom: { style: "thin", color: { argb: "CBD5E1" } },
//         right: { style: "thin", color: { argb: "CBD5E1" } },
//       };

//       const headerRow = worksheet.getRow(1);
//       headerRow.height = 26;
//       headerRow.eachCell((cell) => {
//         cell.font = {
//           bold: true,
//           name: "Arial",
//           size: 11,
//           color: { argb: "FFFFFF" },
//         };
//         cell.fill = {
//           type: "pattern",
//           pattern: "solid",
//           fgColor: { argb: "475569" },
//         };
//         cell.alignment = { vertical: "middle", horizontal: "center" };
//         cell.border = thinBorder;
//       });

//       const personalRateRaw =
//         commissionData.calculationDetails?.rateApplied || "";
//       let personalRateValue = personalRateRaw;
//       let personalRateType = "Flat Rate";

//       if (personalRateRaw.includes("(Marginal)")) {
//         personalRateValue = personalRateRaw.replace(" (Marginal)", "");
//         personalRateType = "Marginal progressive";
//       } else if (personalRateRaw.includes("(Flat Payout)")) {
//         personalRateValue = personalRateRaw.replace(" (Flat Payout)", "");
//         personalRateType = "Flat Bounty Amount";
//       } else if (
//         commissionData.calculationDetails?.calculationType ===
//         "marginal_progressive"
//       ) {
//         personalRateValue = "Progressive Matrix";
//         personalRateType = "Marginal progressive";
//       }

//       const personalRow = worksheet.addRow({
//         id: commissionData.employeeId,
//         name: commissionData.name,
//         sales: Number(commissionData.totalSales) || 0,
//         rateValue: personalRateValue,
//         rateType: personalRateType,
//         comm:
//           Number(
//             commissionData.personalCommission !== undefined
//               ? commissionData.personalCommission
//               : commissionData.calculatedCommission,
//           ) || 0,
//       });
//       personalRow.height = 22;

//       let managerRow = null;
//       if (
//         commissionData.isManager &&
//         commissionData.calculationDetails?.managerTeamCommissions
//       ) {
//         const mgrData =
//           commissionData.calculationDetails.managerTeamCommissions;
//         managerRow = worksheet.addRow({
//           id: commissionData.employeeId,
//           name: commissionData.name,
//           sales: Number(mgrData.teamSalesTotal) || 0,
//           rateValue: mgrData.managerTeamRate,
//           rateType: "Team Override Bonus",
//           comm: Number(mgrData.teamCommissionEarned) || 0,
//         });
//         managerRow.height = 22;
//       }

//       const spacerRow = worksheet.addRow({});
//       spacerRow.height = 18;

//       const totalSalesSum =
//         Number(commissionData.totalSales) +
//         Number(
//           commissionData.calculationDetails?.managerTeamCommissions
//             ?.teamSalesTotal || 0,
//         );
//       const grandTotalRow = worksheet.addRow({
//         id: "",
//         name: "GRAND TOTAL:",
//         sales: totalSalesSum,
//         rateValue: "",
//         rateType: "",
//         comm: Number(commissionData.calculatedCommission) || 0,
//       });
//       grandTotalRow.height = 24;

//       const doubleBottomBorder = {
//         top: { style: "thin", color: { argb: "94A3B8" } },
//         left: { style: "thin", color: { argb: "CBD5E1" } },
//         right: { style: "thin", color: { argb: "CBD5E1" } },
//         bottom: { style: "double", color: { argb: "475569" } },
//       };

//       grandTotalRow.eachCell((cell, colNumber) => {
//         cell.font = { bold: true, name: "Arial", size: 11 };
//         cell.border = doubleBottomBorder;
//         if (colNumber === 2) {
//           cell.alignment = { horizontal: "right", vertical: "middle" };
//         }
//       });

//       const ACCOUNTING_MASK =
//         '_($* #,##0.00_);_($* (#,##0.00);_($* "-"??_);_(@_)';

//       worksheet.eachRow((row, rowNumber) => {
//         if (rowNumber === 1) return;

//         const totalDataRows = managerRow ? 3 : 2;
//         if (rowNumber <= totalDataRows) {
//           row.eachCell((cell) => {
//             cell.border = thinBorder;
//             cell.alignment = { vertical: "middle" };
//           });
//         }

//         const idCell = row.getCell("id");
//         const salesCell = row.getCell("sales");
//         const rateValueCell = row.getCell("rateValue");
//         const rateTypeCell = row.getCell("rateType");
//         const commCell = row.getCell("comm");

//         if (idCell.value)
//           idCell.alignment = { horizontal: "center", vertical: "middle" };
//         if (rateValueCell.value && rowNumber !== grandTotalRow.number) {
//           rateValueCell.alignment = {
//             horizontal: "center",
//             vertical: "middle",
//           };
//         }
//         if (rateTypeCell.value) {
//           rateTypeCell.alignment = { horizontal: "center", vertical: "middle" };
//         }

//         if (typeof salesCell.value === "number") {
//           salesCell.numFmt = ACCOUNTING_MASK;
//           salesCell.alignment = { horizontal: "right", vertical: "middle" };
//         }
//         if (typeof commCell.value === "number") {
//           commCell.numFmt = ACCOUNTING_MASK;
//           commCell.alignment = { horizontal: "right", vertical: "middle" };
//         }
//       });

//       const buffer = await workbook.xlsx.writeBuffer();
//       const blob = new Blob([buffer], {
//         type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
//       });
//       const link = document.createElement("a");
//       link.href = URL.createObjectURL(blob);

//       const cleanName = commissionData.name
//         .replace(/\s+/g, "_")
//         .replace(/[^a-zA-Z0-9_]/g, "");
//       link.download = `Commission_Report_${cleanName}_${periodLabel.replace(/\s+/g, "_")}.xlsx`;

//       document.body.appendChild(link);
//       link.click();
//       document.body.removeChild(link);
//     } catch (err) {
//       console.error("Excel Active Export Runtime Error:", err.message);
//     } finally {
//       setExporting(false);
//     }
//   };

//   const formatPeriodLabel = (label) => {
//     if (!label) return "";
//     const clean = label.toLowerCase().replace("-", "_");
//     if (
//       clean === "semi_annual" ||
//       clean === "semi_annually" ||
//       clean === "semi_yearly"
//     ) {
//       return "Semi-Annual";
//     }
//     return label.charAt(0).toUpperCase() + label.slice(1);
//   };

//   const formatCurrency = (val) => {
//     return new Intl.NumberFormat("en-US", {
//       style: "currency",
//       currency: "USD",
//       minimumFractionDigits: 2,
//       maximumFractionDigits: 2,
//     }).format(val || 0);
//   };

//   if (initialFetchLoading) {
//     return (
//       <div className="min-h-screen flex items-center justify-center bg-slate-50">
//         <div className="text-center text-slate-400 space-y-3">
//           <RefreshCw className="w-6 h-6 animate-spin mx-auto text-slate-900" />
//           <p className="text-xs font-medium tracking-wide uppercase">
//             Initializing System...
//           </p>
//         </div>
//       </div>
//     );
//   }

//   return (
//     <div className="min-h-screen bg-slate-50 flex text-slate-900 font-sans antialiased">
//       {/* Print styles */}
//       <style>{`
//         @media print {
//           @page { size: letter; margin: 0.5in !important; }
//           html, body { background: #fff !important; color: #000 !important; height: auto !important; min-height: 0 !important; overflow: visible !important; }
//           aside, .print\\:hide, button, a, input, select { display: none !important; }
//           main { padding: 0 !important; margin: 0 !important; max-width: 100% !important; width: 100% !important; overflow: visible !important; }
//           .grid { display: grid !important; gap: 15px !important; grid-template-columns: repeat(2, 1fr) !important; page-break-inside: avoid !important; margin-bottom: 20px !important; }
//           .bg-white { border: 1px solid #cbd5e1 !important; box-shadow: none !important; background: #fff !important; page-break-inside: avoid !important; }
//           tr { page-break-inside: avoid !important; }
//         }
//       `}</style>

//       {/* ─── SIDEBAR ─── */}
//       <aside className="w-64 bg-white border-r border-slate-100 flex flex-col shrink-0 sticky top-0 h-screen">
//         <div className="p-5 border-b border-slate-100">
//           <div className="flex items-center gap-2">
//             <div className="w-2.5 h-2.5 bg-indigo-600 rounded-full" />
//             <h2 className="text-xs font-bold uppercase tracking-wider text-slate-400">
//               Contractors
//             </h2>
//           </div>
//         </div>

//         <nav className="flex-1 overflow-y-auto p-3 space-y-1">
//           {builders.map((builder) => (
//             <button
//               key={builder.salesmanId}
//               onClick={() => handleTabChange(builder)}
//               className={`w-full px-3 py-2.5 text-sm font-medium rounded-lg transition flex items-start gap-3 text-left ${
//                 activeBuilderId === builder.salesmanId
//                   ? "bg-slate-900 text-white shadow-sm font-semibold"
//                   : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
//               }`}
//             >
//               {builder.isManager ? (
//                 <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0 text-blue-500" />
//               ) : (
//                 <User
//                   className={`w-4 h-4 mt-0.5 shrink-0 ${activeBuilderId === builder.salesmanId ? "text-indigo-400" : "text-slate-400"}`}
//                 />
//               )}
//               <div className="truncate">
//                 <p className="leading-tight truncate">{builder.name}</p>
//                 <p className="text-[10px] uppercase tracking-wider font-medium mt-0.5 text-slate-400">
//                   {formatPeriodLabel(builder.periodType)}{" "}
//                   {builder.isManager && "(Manager)"}
//                 </p>
//               </div>
//             </button>
//           ))}
//         </nav>
//       </aside>

//       {/* ─── MAIN CONTENT AREA ─── */}
//       <main className="flex-1 p-8 space-y-6 overflow-x-hidden max-w-6xl">
//         {/* Header Toolbar */}
//         <div className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-200 pb-5">
//           <div>
//             <h1 className="text-2xl font-bold tracking-tight text-slate-900">
//               Builders Commission
//             </h1>
//             <p className="text-slate-500 text-sm">
//               {selectedBuilder?.name || "Builder Summary"} • {periodLabel}
//             </p>
//           </div>

//           <div className="flex items-center gap-3 print:hide">
//             <input
//               type="number"
//               min="2000"
//               max="2100"
//               value={year}
//               onChange={(e) => setYear(Number(e.target.value))}
//               className="border border-slate-200 bg-white rounded-lg px-2.5 py-1.5 w-20 text-sm font-semibold text-center focus:outline-none focus:ring-1 focus:ring-slate-400"
//             />

//             <select
//               value={period}
//               onChange={(e) => setPeriod(e.target.value)}
//               className="border border-slate-200 bg-white rounded-lg px-3 py-1.5 text-sm font-medium focus:outline-none focus:ring-1 focus:ring-slate-400"
//             >
//               <optgroup label="Global Options">
//                 <option value="yearly">Full Year</option>
//                 <option value="ytd">YTD Accumulation</option>
//               </optgroup>

//               {selectedBuilder?.periodType === "monthly" && (
//                 <optgroup label="Months">
//                   {[
//                     "January",
//                     "February",
//                     "March",
//                     "April",
//                     "May",
//                     "June",
//                     "July",
//                     "August",
//                     "September",
//                     "October",
//                     "November",
//                     "December",
//                   ].map((m, i) => (
//                     <option key={i} value={`month-${i + 1}`}>
//                       {m}
//                     </option>
//                   ))}
//                 </optgroup>
//               )}

//               {selectedBuilder?.periodType === "quarterly" && (
//                 <optgroup label="Quarters">
//                   <option value="quarter-q1">Q1 Block</option>
//                   <option value="quarter-q2">Q2 Block</option>
//                   <option value="quarter-q3">Q3 Block</option>
//                   <option value="quarter-q4">Q4 Block</option>
//                 </optgroup>
//               )}

//               {(selectedBuilder?.periodType === "semi_annual" ||
//                 selectedBuilder?.periodType === "semi-annually" ||
//                 selectedBuilder?.periodType === "semi-annual") && (
//                 <optgroup label="Half-Year Cycles">
//                   <option value="h1">First Half (H1)</option>
//                   <option value="h2">Second Half (H2)</option>
//                 </optgroup>
//               )}
//             </select>

//             <Link
//               to="/builders/setting"
//               className="flex items-center gap-2 px-3.5 py-2 border border-slate-200 bg-white hover:border-indigo-200 hover:bg-indigo-50/50 active:scale-[0.98] text-slate-600 hover:text-indigo-600 rounded-lg text-sm font-semibold shadow-sm transition-all duration-150 ease-in-out select-none print:hide"
//               title="Builder Settings"
//             >
//               <Settings className="w-4 h-4 shrink-0" />
//               <span>Settings</span>
//             </Link>

//             <button
//               onClick={exportAllToExcel}
//               disabled={exporting || !commissionData}
//               className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 active:scale-[0.98] disabled:bg-slate-100 disabled:text-slate-400 disabled:scale-100 text-white rounded-lg text-sm font-semibold shadow-sm transition-all duration-150 ease-in-out cursor-pointer select-none"
//             >
//               {exporting ? (
//                 <RefreshCw className="w-4 h-4 text-emerald-200 animate-spin shrink-0" />
//               ) : (
//                 <Download className="w-4 h-4 text-white shrink-0" />
//               )}
//               <span>
//                 {exporting ? "Exporting Ledger..." : "Export Current Tab"}
//               </span>
//             </button>
//           </div>
//         </div>

//         {/* Display Workspace */}
//         {loading ? (
//           <div className="py-24 text-center">
//             <RefreshCw className="w-6 h-6 animate-spin mx-auto text-slate-300 mb-2" />
//             <p className="text-slate-400 text-xs uppercase tracking-wider font-semibold">
//               Calculating Data...
//             </p>
//           </div>
//         ) : commissionData ? (
//           <div className="space-y-6">
//             {/* Cards */}
//             <div
//               className={`grid grid-cols-1 ${commissionData.isManager ? "md:grid-cols-4" : "md:grid-cols-3"} gap-6`}
//             >
//               <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
//                 <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
//                   Total Sales
//                 </p>
//                 <p className="text-2xl font-bold text-slate-900 tracking-tight mt-1">
//                   {formatCurrency(commissionData.totalSales)}
//                 </p>
//               </div>

//               {commissionData.isManager && (
//                 <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
//                   <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
//                     Personal Commission
//                   </p>
//                   <p className="text-2xl font-bold text-slate-900 tracking-tight mt-1">
//                     {formatCurrency(commissionData.personalCommission)}
//                   </p>
//                 </div>
//               )}

//               {commissionData.isManager && (
//                 <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm flex flex-col justify-between">
//                   <div>
//                     <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
//                       Team Commission
//                     </p>
//                     <p className="text-2xl font-bold text-slate-900 tracking-tight mt-1">
//                       {formatCurrency(commissionData.teamCommissionEarned)}
//                     </p>
//                     <p className="text-[11px] text-slate-400 mt-1 font-medium">
//                       Rate:{" "}
//                       {commissionData.calculationDetails?.managerTeamCommissions
//                         ?.managerTeamRate || "0%"}{" "}
//                       • Team Total Vol (Inc. Self):{" "}
//                       {formatCurrency(
//                         commissionData.calculationDetails
//                           ?.managerTeamCommissions?.teamSalesTotal,
//                       )}
//                     </p>
//                   </div>
//                   <button
//                     type="button"
//                     onClick={() => setShowModalBreakdown(true)}
//                     className="mt-3 text-left w-max text-xs font-bold text-indigo-600 hover:text-indigo-800 bg-transparent border-none p-0 cursor-pointer outline-none transition select-none print:hide"
//                   >
//                     View Details &rarr;
//                   </button>
//                 </div>
//               )}

//               <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
//                 <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
//                   {commissionData.isManager ? "Total Commission" : "Commission"}
//                 </p>
//                 <p className="text-2xl font-bold text-slate-900 tracking-tight mt-1">
//                   {formatCurrency(commissionData.calculatedCommission)}
//                 </p>
//               </div>
//             </div>

//             {/* Matrix Table */}
//             <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
//               <div className="border-b border-slate-100 px-6 py-4">
//                 <h3 className="text-sm font-semibold text-slate-800">
//                   Rate Allocation Matrix
//                 </h3>
//               </div>

//               <div className="overflow-x-auto">
//                 <table className="w-full text-left border-collapse">
//                   <thead>
//                     <tr className="bg-slate-50/70 text-[11px] font-bold uppercase tracking-wider text-slate-400 border-b border-slate-100">
//                       <th className="py-3 px-6">Tier Level</th>
//                       <th className="py-3 px-6">Sales Volume Window</th>
//                       <th className="py-3 px-6 text-right">Commission Rate</th>
//                     </tr>
//                   </thead>
//                   <tbody className="divide-y divide-slate-100 text-sm">
//                     {selectedBuilder?.brackets?.map((bracket, index) => {
//                       const isCurrent =
//                         commissionData.totalSales >= bracket.from &&
//                         commissionData.totalSales <= bracket.to;

//                       return (
//                         <tr
//                           key={index}
//                           className={`transition ${isCurrent ? "bg-indigo-50/30 font-medium" : "text-slate-600"}`}
//                         >
//                           <td className="py-4 px-6 relative">
//                             {isCurrent && (
//                               <div className="absolute left-0 top-0 bottom-0 w-1 bg-indigo-600" />
//                             )}
//                             <span
//                               className={
//                                 isCurrent
//                                   ? "text-indigo-600 font-semibold"
//                                   : "text-slate-900"
//                               }
//                             >
//                               Tier {index + 1}
//                             </span>
//                           </td>
//                           <td className="py-4 px-6">
//                             <div className="flex items-center gap-2 text-slate-500">
//                               <span className="text-slate-900 font-mono">
//                                 {formatCurrency(bracket.from)}
//                               </span>
//                               <ArrowRight className="w-3.5 h-3.5 opacity-50" />
//                               <span className="font-mono">
//                                 {bracket.to >= 999999999
//                                   ? "Unlimited"
//                                   : formatCurrency(bracket.to)}
//                               </span>
//                             </div>
//                           </td>
//                           <td className="py-4 px-6 text-right">
//                             <span
//                               className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${isCurrent ? "bg-indigo-100 text-indigo-800" : "bg-slate-100 text-slate-700"}`}
//                             >
//                               {Number(bracket.rate).toFixed(2)}%{" "}
//                             </span>
//                           </td>
//                         </tr>
//                       );
//                     })}
//                   </tbody>
//                 </table>
//               </div>
//             </div>

//             {/* ─── MODAL BREAKDOWN OVERLAY DIALOG ─── */}
//             {showModalBreakdown &&
//               commissionData.isManager &&
//               commissionData.calculationDetails?.managerTeamCommissions
//                 ?.rosterBreakdown && (
//                 <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm animate-fadeIn print:hide">
//                   <div className="bg-white border border-slate-200 rounded-2xl shadow-xl w-full max-w-3xl overflow-hidden flex flex-col max-h-[85vh]">
//                     <div className="flex justify-between items-center border-b border-slate-100 px-6 py-4 sticky top-0 bg-white z-10">
//                       <h3 className="text-sm font-bold text-slate-800">
//                         Team Sales Volume Breakdown — {periodLabel}
//                       </h3>
//                       <button
//                         type="button"
//                         onClick={() => setShowModalBreakdown(false)}
//                         className="text-slate-400 hover:text-slate-600 p-1.5 hover:bg-slate-50 rounded-lg transition cursor-pointer border-none bg-transparent"
//                       >
//                         <X className="w-4 h-4" />
//                       </button>
//                     </div>

//                     <div className="overflow-y-auto flex-1 p-2">
//                       <table className="w-full text-left border-collapse">
//                         <thead>
//                           <tr className="bg-slate-50/70 text-[10px] font-bold uppercase tracking-wider text-slate-400 border-b border-slate-100">
//                             <th className="py-2.5 px-4 w-28">Salesman ID</th>
//                             <th className="py-2.5 px-4">
//                               Roster Contractor Name
//                             </th>
//                             <th className="py-2.5 px-4 text-right">
//                               Period Sales Volume
//                             </th>
//                           </tr>
//                         </thead>
//                         <tbody className="divide-y divide-slate-100 text-sm text-slate-600">
//                           {commissionData.calculationDetails.managerTeamCommissions.rosterBreakdown.map(
//                             (member, mIdx) => (
//                               <tr
//                                 key={mIdx}
//                                 className={
//                                   member.isManagerSelf
//                                     ? "bg-blue-50/20 font-medium"
//                                     : ""
//                                 }
//                               >
//                                 <td className="py-3 px-4 font-mono font-bold text-slate-400">
//                                   {member.salesmanId}
//                                 </td>
//                                 <td className="py-3 px-4 font-bold text-slate-800">
//                                   {member.name}{" "}
//                                   {member.isManagerSelf && (
//                                     <span className="text-[9px] ml-1 px-1 py-0.2 text-blue-600 bg-blue-100 rounded font-extrabold uppercase">
//                                       Self
//                                     </span>
//                                   )}
//                                 </td>
//                                 <td className="py-3 px-4 text-right font-mono text-slate-900 font-semibold">
//                                   {formatCurrency(member.salesVolume)}
//                                 </td>
//                               </tr>
//                             ),
//                           )}
//                         </tbody>
//                       </table>
//                     </div>

//                     <div className="flex justify-end px-6 py-3 border-t border-slate-100 bg-slate-50 rounded-b-2xl">
//                       <button
//                         type="button"
//                         onClick={() => setShowModalBreakdown(false)}
//                         className="px-4 py-1.5 bg-slate-900 hover:bg-slate-800 text-white rounded-lg font-bold text-xs shadow-sm transition cursor-pointer outline-none border-none"
//                       >
//                         Close Roster view
//                       </button>
//                     </div>
//                   </div>
//                 </div>
//               )}
//           </div>
//         ) : (
//           <div className="bg-white border border-slate-200 rounded-xl p-16 text-center shadow-sm text-slate-400">
//             <Layers className="w-6 h-6 mx-auto mb-2 opacity-40 text-slate-900" />
//             <p className="text-sm font-medium text-slate-600">
//               No report metrics loaded.
//             </p>
//             <p className="text-slate-400 text-xs mt-0.5">
//               Please check parameter inputs and sync parameters.
//             </p>
//           </div>
//         )}
//       </main>
//     </div>
//   );
// };

// export default BuildersCommission;
// // import React, { useState, useMemo, useEffect } from "react";
// // import {
// //   Download,
// //   Layers,
// //   RefreshCw,
// //   User,
// //   Settings,
// //   ArrowRight,
// //   ShieldAlert,
// //   X,
// // } from "lucide-react";
// // import { Link } from "react-router-dom";
// // import * as XLSX from "xlsx"; // SheetJS import hook block

// // const BuildersCommission = () => {
// //   // ─── Core Application States ──────────────────────────────────────────────────
// //   const [builders, setBuilders] = useState([]);
// //   const [activeBuilderId, setActiveBuilderId] = useState(null);
// //   const [commissionData, setCommissionData] = useState(null);

// //   const [year, setYear] = useState(new Date().getFullYear());
// //   const [period, setPeriod] = useState("yearly");
// //   const [loading, setLoading] = useState(false);
// //   const [initialFetchLoading, setInitialFetchLoading] = useState(true);
// //   const [exporting, setExporting] = useState(false);

// //   // Toggle state indicator for modal popup window display
// //   const [showModalBreakdown, setShowModalBreakdown] = useState(false);

// //   // ─── REFACTORED ACCOUNTING ENGINE FOR PREVIOUS SYSTEM PERIODS ─────────────────
// //   const getCalculatedPeriodAndYear = (type) => {
// //     if (!type)
// //       return {
// //         computedPeriod: "yearly",
// //         computedYear: new Date().getFullYear(),
// //       };

// //     const normalizedType = type.toLowerCase().replace("-", "_");
// //     const currentDate = new Date();
// //     const currentMonth = currentDate.getMonth();

// //     let computedPeriod = "yearly";
// //     let computedYear = currentDate.getFullYear();

// //     if (normalizedType === "monthly") {
// //       if (currentMonth === 0) {
// //         computedPeriod = "month-12";
// //         computedYear = currentDate.getFullYear() - 1;
// //       } else {
// //         computedPeriod = `month-${currentMonth}`;
// //       }
// //     } else if (normalizedType === "quarterly") {
// //       const currentQuarter = Math.floor(currentMonth / 3) + 1;
// //       if (currentQuarter === 1) {
// //         computedPeriod = "quarter-q4";
// //         computedYear = currentDate.getFullYear() - 1;
// //       } else {
// //         computedPeriod = `quarter-q${currentQuarter - 1}`;
// //       }
// //     } else if (
// //       normalizedType === "semi_annually" ||
// //       normalizedType === "semi_annual" ||
// //       normalizedType === "semi_yearly"
// //     ) {
// //       if (currentMonth < 6) {
// //         computedPeriod = "h2";
// //         computedYear = currentDate.getFullYear() - 1;
// //       } else {
// //         computedPeriod = "h1";
// //       }
// //     }

// //     return { computedPeriod, computedYear };
// //   };

// //   // ─── Fetch Builders Database Profiles on Mount ──────────────────────────────
// //   useEffect(() => {
// //     const fetchBuildersList = async () => {
// //       try {
// //         const res = await fetch("http://10.5.21.42:5000/api/builders");
// //         if (!res.ok)
// //           throw new Error("Could not retrieve builders directory data");
// //         const data = await res.json();

// //         setBuilders(data);
// //         if (data.length > 0) {
// //           const defaultBuilder = data[0];
// //           setActiveBuilderId(defaultBuilder.salesmanId);

// //           const { computedPeriod, computedYear } = getCalculatedPeriodAndYear(
// //             defaultBuilder.periodType,
// //           );
// //           setPeriod(computedPeriod);
// //           setYear(computedYear);

// //           fetchReport(computedYear, computedPeriod, defaultBuilder.salesmanId);
// //         }
// //       } catch (err) {
// //         console.error("Initialization Error:", err.message);
// //       } finally {
// //         setInitialFetchLoading(false);
// //       }
// //     };
// //     fetchBuildersList();
// //   }, []);

// //   const selectedBuilder = useMemo(() => {
// //     return builders.find((b) => b.salesmanId === activeBuilderId) || null;
// //   }, [builders, activeBuilderId]);

// //   // Helper method creating query strings parameters securely
// //   const buildQueryParameters = (targetYear, periodValue, salesmanId) => {
// //     const params = new URLSearchParams({
// //       year: String(targetYear),
// //       salesmanId: String(salesmanId),
// //     });

// //     if (periodValue.startsWith("month-")) {
// //       params.append("month", periodValue.split("-")[1]);
// //     } else if (periodValue.startsWith("quarter-")) {
// //       params.append("quarter", periodValue.split("-")[1].toUpperCase());
// //     } else if (periodValue === "h1" || periodValue === "h2") {
// //       params.append("half", periodValue.toUpperCase());
// //     } else if (periodValue === "ytd") {
// //       params.append("ytd", "true");
// //     } else if (periodValue === "yearly") {
// //       params.append("yearly", "true");
// //     }
// //     return params.toString();
// //   };

// //   // ─── Unified Report Processing Request Pipeline ──────────────────────────────
// //   const fetchReport = async (targetYear, periodValue, salesmanId) => {
// //     const targetId = salesmanId || activeBuilderId;
// //     if (!targetId) return;

// //     setLoading(true);
// //     try {
// //       const queryString = buildQueryParameters(
// //         targetYear,
// //         periodValue,
// //         targetId,
// //       );
// //       const res = await fetch(
// //         `http://10.5.21.42:5000/api/builders/commissions?${queryString}`,
// //       );

// //       if (!res.ok) throw new Error("Failed to fetch commission report");
// //       const json = await res.json();
// //       setCommissionData(json.data || json);
// //     } catch (err) {
// //       console.error("API Pipeline Error:", err.message);
// //       setCommissionData(null);
// //     } finally {
// //       setLoading(false);
// //     }
// //   };

// //   // ─── SMART FILTER-PERSISTING TAB CHANGE ───
// //   const handleTabChange = (nextBuilderProfile) => {
// //     const nextId = nextBuilderProfile.salesmanId;
// //     setActiveBuilderId(nextId);
// //     setShowModalBreakdown(false);

// //     const nextType = nextBuilderProfile.periodType
// //       ? nextBuilderProfile.periodType.toLowerCase().replace("-", "_")
// //       : "";

// //     const isUniversalFilter = period === "yearly" || period === "ytd";
// //     const isMonthlyMatch =
// //       period.startsWith("month-") && nextType === "monthly";
// //     const isQuarterlyMatch =
// //       period.startsWith("quarter-") && nextType === "quarterly";
// //     const isSemiAnnualMatch =
// //       (period === "h1" || period === "h2") &&
// //       (nextType === "semi_annual" || nextType === "semi_annually");

// //     if (
// //       isUniversalFilter ||
// //       isMonthlyMatch ||
// //       isQuarterlyMatch ||
// //       isSemiAnnualMatch
// //     ) {
// //       fetchReport(year, period, nextId);
// //     } else {
// //       const { computedPeriod, computedYear } = getCalculatedPeriodAndYear(
// //         nextBuilderProfile.periodType,
// //       );
// //       setPeriod(computedPeriod);
// //       setYear(computedYear);
// //       fetchReport(computedYear, computedPeriod, nextId);
// //     }
// //   };

// //   useEffect(() => {
// //     if (activeBuilderId && !initialFetchLoading) {
// //       fetchReport(year, period, activeBuilderId);
// //     }
// //   }, [year, period]);

// //   // ─── COMPREHENSIVE EXCEL EXPORT WORKFLOW SYSTEM ───────────────────────────
// //   // ─── PERIOD-GROUPED COMPREHENSIVE EXCEL EXPORT WORKFLOW ───────────────────
// //   // ─── ACTIVE TAB ONLY EXCEL EXPORT WORKFLOW ────────────────────────────────
// //   const exportAllToExcel = async () => {
// //     // Guard clause: Ensure we actually have data loaded for the active builder
// //     if (!commissionData || !selectedBuilder) return;
// //     setExporting(true);

// //     try {
// //       const ExcelJS = require("exceljs");
// //       const workbook = new ExcelJS.Workbook();

// //       // Use the active builder's name for the sheet tab title (Max 31 characters)
// //       const tabTitle = selectedBuilder.name
// //         .replace(/[\[\]\*\?\:\/\\]/g, "")
// //         .substring(0, 30);

// //       // Create worksheet with single-page print scaling constraints forced
// //       const worksheet = workbook.addWorksheet(tabTitle, {
// //         pageSetup: {
// //           orientation: "landscape",
// //           paperSize: 9, // Standard Letter size
// //           fitToPage: true, // Turn scaling engine ON
// //           fitToWidth: 1, // Force exactly 1 page wide
// //           fitToHeight: 1, // Force exactly 1 page tall
// //         },
// //         views: [{ showGridLines: true }],
// //       });

// //       // Define columns
// //       worksheet.columns = [
// //         { header: "Salesman ID", key: "id", width: 15 },
// //         { header: "Salesman Name", key: "name", width: 30 },
// //         { header: "Total Sales", key: "sales", width: 24 },
// //         { header: "Rate (%) / Amount ($)", key: "rateValue", width: 22 },
// //         { header: "Rate Type", key: "rateType", width: 22 },
// //         { header: "Commission Earned", key: "comm", width: 24 },
// //       ];

// //       const thinBorder = {
// //         top: { style: "thin", color: { argb: "CBD5E1" } },
// //         left: { style: "thin", color: { argb: "CBD5E1" } },
// //         bottom: { style: "thin", color: { argb: "CBD5E1" } },
// //         right: { style: "thin", color: { argb: "CBD5E1" } },
// //       };

// //       // Format Header Row (Slate Gray background with White typography)
// //       const headerRow = worksheet.getRow(1);
// //       headerRow.height = 26;
// //       headerRow.eachCell((cell) => {
// //         cell.font = {
// //           bold: true,
// //           name: "Arial",
// //           size: 11,
// //           color: { argb: "FFFFFF" },
// //         };
// //         cell.fill = {
// //           type: "pattern",
// //           pattern: "solid",
// //           fgColor: { argb: "475569" },
// //         };
// //         cell.alignment = { vertical: "middle", horizontal: "center" };
// //         cell.border = thinBorder;
// //       });

// //       // Parse the active bracket rate string patterns safely
// //       const personalRateRaw =
// //         commissionData.calculationDetails?.rateApplied || "";
// //       let personalRateValue = personalRateRaw;
// //       let personalRateType = "Flat Rate";

// //       if (personalRateRaw.includes("(Marginal)")) {
// //         personalRateValue = personalRateRaw.replace(" (Marginal)", "");
// //         personalRateType = "Marginal progressive";
// //       } else if (personalRateRaw.includes("(Flat Payout)")) {
// //         personalRateValue = personalRateRaw.replace(" (Flat Payout)", "");
// //         personalRateType = "Flat Bounty Amount";
// //       } else if (
// //         commissionData.calculationDetails?.calculationType ===
// //         "marginal_progressive"
// //       ) {
// //         personalRateValue = "Progressive Matrix";
// //         personalRateType = "Marginal progressive";
// //       }

// //       // Row 1: Add Personal Metrics Row
// //       const personalRow = worksheet.addRow({
// //         id: commissionData.employeeId,
// //         name: commissionData.name,
// //         sales: Number(commissionData.totalSales) || 0,
// //         rateValue: personalRateValue,
// //         rateType: personalRateType,
// //         comm:
// //           Number(
// //             commissionData.personalCommission !== undefined
// //               ? commissionData.personalCommission
// //               : commissionData.calculatedCommission,
// //           ) || 0,
// //       });
// //       personalRow.height = 22;

// //       // Row 2: Add Manager Override metrics row if active for this salesperson
// //       let managerRow = null;
// //       if (
// //         commissionData.isManager &&
// //         commissionData.calculationDetails?.managerTeamCommissions
// //       ) {
// //         const mgrData =
// //           commissionData.calculationDetails.managerTeamCommissions;
// //         managerRow = worksheet.addRow({
// //           id: commissionData.employeeId,
// //           name: commissionData.name,
// //           sales: Number(mgrData.teamSalesTotal) || 0,
// //           rateValue: mgrData.managerTeamRate,
// //           rateType: "Team Override Bonus",
// //           comm: Number(mgrData.teamCommissionEarned) || 0,
// //         });
// //         managerRow.height = 22;
// //       }

// //       // Add Spacer Padding row
// //       const spacerRow = worksheet.addRow({});
// //       spacerRow.height = 18;

// //       // Add Grand Total Remittance Summary Row
// //       const totalSalesSum =
// //         Number(commissionData.totalSales) +
// //         Number(
// //           commissionData.calculationDetails?.managerTeamCommissions
// //             ?.teamSalesTotal || 0,
// //         );
// //       const grandTotalRow = worksheet.addRow({
// //         id: "",
// //         name: "GRAND TOTAL:",
// //         sales: totalSalesSum,
// //         rateValue: "",
// //         rateType: "",
// //         comm: Number(commissionData.calculatedCommission) || 0,
// //       });
// //       grandTotalRow.height = 24;

// //       // Double underline accounting border layout configurations
// //       const doubleBottomBorder = {
// //         top: { style: "thin", color: { argb: "94A3B8" } },
// //         left: { style: "thin", color: { argb: "CBD5E1" } },
// //         right: { style: "thin", color: { argb: "CBD5E1" } },
// //         bottom: { style: "double", color: { argb: "475569" } },
// //       };

// //       grandTotalRow.eachCell((cell, colNumber) => {
// //         cell.font = { bold: true, name: "Arial", size: 11 };
// //         cell.border = doubleBottomBorder;
// //         if (colNumber === 2) {
// //           cell.alignment = { horizontal: "right", vertical: "middle" };
// //         }
// //       });

// //       // Apply currency formatting cells loops maps
// //       const ACCOUNTING_MASK =
// //         '_($* #,##0.00_);_($* (#,##0.00);_($* "-"??_);_(@_)';

// //       worksheet.eachRow((row, rowNumber) => {
// //         if (rowNumber === 1) return; // Skip headers

// //         const totalDataRows = managerRow ? 3 : 2;
// //         if (rowNumber <= totalDataRows) {
// //           row.eachCell((cell) => {
// //             cell.border = thinBorder;
// //             cell.alignment = { vertical: "middle" };
// //           });
// //         }

// //         const idCell = row.getCell("id");
// //         const salesCell = row.getCell("sales");
// //         const rateValueCell = row.getCell("rateValue");
// //         const rateTypeCell = row.getCell("rateType");
// //         const commCell = row.getCell("comm");

// //         if (idCell.value)
// //           idCell.alignment = { horizontal: "center", vertical: "middle" };
// //         if (rateValueCell.value && rowNumber !== grandTotalRow.number) {
// //           rateValueCell.alignment = {
// //             horizontal: "center",
// //             vertical: "middle",
// //           };
// //         }
// //         if (rateTypeCell.value) {
// //           rateTypeCell.alignment = { horizontal: "center", vertical: "middle" };
// //         }

// //         if (typeof salesCell.value === "number") {
// //           salesCell.numFmt = ACCOUNTING_MASK;
// //           salesCell.alignment = { horizontal: "right", vertical: "middle" };
// //         }
// //         if (typeof commCell.value === "number") {
// //           commCell.numFmt = ACCOUNTING_MASK;
// //           commCell.alignment = { horizontal: "right", vertical: "middle" };
// //         }
// //       });

// //       // Write compiled binary array file down to the hardware device storage layer
// //       const buffer = await workbook.xlsx.writeBuffer();
// //       const blob = new Blob([buffer], {
// //         type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
// //       });
// //       const link = document.createElement("a");
// //       link.href = URL.createObjectURL(blob);

// //       // Name formats: e.g. "Commission_Report_JACOB_WRIGHT_H1_2026.xlsx"
// //       const cleanName = commissionData.name
// //         .replace(/\s+/g, "_")
// //         .replace(/[^a-zA-Z0-9_]/g, "");
// //       link.download = `Commission_Report_${cleanName}_${periodLabel.replace(/\s+/g, "_")}.xlsx`;

// //       document.body.appendChild(link);
// //       link.click();
// //       document.body.removeChild(link);
// //     } catch (err) {
// //       console.error("Excel Active Export Runtime Error:", err.message);
// //     } finally {
// //       setExporting(false);
// //     }
// //   };

// //   const periodLabel = useMemo(() => {
// //     if (period === "yearly") return `Full Year ${year}`;
// //     if (period === "ytd") return `Year to Date (YTD) ${year}`;
// //     if (period === "h1") return `First Half (H1) ${year}`;
// //     if (period === "h2") return `Second Half (H2) ${year}`;
// //     if (period.startsWith("quarter-"))
// //       return `${period.split("-")[1].toUpperCase()} ${year}`;
// //     if (period.startsWith("month-")) {
// //       const m = Number(period.split("-")[1]);
// //       const months = [
// //         "January",
// //         "February",
// //         "March",
// //         "April",
// //         "May",
// //         "June",
// //         "July",
// //         "August",
// //         "September",
// //         "October",
// //         "November",
// //         "December",
// //       ];
// //       return `${months[m - 1]} ${year}`;
// //     }
// //     return `${year}`;
// //   }, [period, year]);

// //   const formatPeriodLabel = (label) => {
// //     if (!label) return "";
// //     const clean = label.toLowerCase().replace("-", "_");
// //     if (
// //       clean === "semi_annual" ||
// //       clean === "semi_annually" ||
// //       clean === "semi_yearly"
// //     ) {
// //       return "Semi-Annual";
// //     }
// //     return label.charAt(0).toUpperCase() + label.slice(1);
// //   };

// //   const formatCurrency = (val) => {
// //     return new Intl.NumberFormat("en-US", {
// //       style: "currency",
// //       currency: "USD",
// //       minimumFractionDigits: 2,
// //       maximumFractionDigits: 2,
// //     }).format(val || 0);
// //   };

// //   if (initialFetchLoading) {
// //     return (
// //       <div className="min-h-screen flex items-center justify-center bg-slate-50">
// //         <div className="text-center text-slate-400 space-y-3">
// //           <RefreshCw className="w-6 h-6 animate-spin mx-auto text-slate-900" />
// //           <p className="text-xs font-medium tracking-wide uppercase">
// //             Initializing System...
// //           </p>
// //         </div>
// //       </div>
// //     );
// //   }

// //   return (
// //     <div className="min-h-screen bg-slate-50 flex text-slate-900 font-sans antialiased">
// //       {/* Print styles */}
// //       <style>{`
// //         @media print {
// //           @page { size: letter; margin: 0.5in !important; }
// //           html, body { background: #fff !important; color: #000 !important; height: auto !important; min-height: 0 !important; overflow: visible !important; }
// //           aside, .print\\:hide, button, a, input, select { display: none !important; }
// //           main { padding: 0 !important; margin: 0 !important; max-width: 100% !important; width: 100% !important; overflow: visible !important; }
// //           .grid { display: grid !important; gap: 15px !important; grid-template-columns: repeat(2, 1fr) !important; page-break-inside: avoid !important; margin-bottom: 20px !important; }
// //           .bg-white { border: 1px solid #cbd5e1 !important; box-shadow: none !important; background: #fff !important; page-break-inside: avoid !important; }
// //           tr { page-break-inside: avoid !important; }
// //         }
// //       `}</style>

// //       {/* ─── SIDEBAR ─── */}
// //       <aside className="w-64 bg-white border-r border-slate-100 flex flex-col shrink-0 sticky top-0 h-screen">
// //         <div className="p-5 border-b border-slate-100">
// //           <div className="flex items-center gap-2">
// //             <div className="w-2.5 h-2.5 bg-indigo-600 rounded-full" />
// //             <h2 className="text-xs font-bold uppercase tracking-wider text-slate-400">
// //               Contractors
// //             </h2>
// //           </div>
// //         </div>

// //         <nav className="flex-1 overflow-y-auto p-3 space-y-1">
// //           {builders.map((builder) => (
// //             <button
// //               key={builder.salesmanId}
// //               onClick={() => handleTabChange(builder)}
// //               className={`w-full px-3 py-2.5 text-sm font-medium rounded-lg transition flex items-start gap-3 text-left ${
// //                 activeBuilderId === builder.salesmanId
// //                   ? "bg-slate-900 text-white shadow-sm font-semibold"
// //                   : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
// //               }`}
// //             >
// //               {builder.isManager ? (
// //                 <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0 text-blue-500" />
// //               ) : (
// //                 <User
// //                   className={`w-4 h-4 mt-0.5 shrink-0 ${activeBuilderId === builder.salesmanId ? "text-indigo-400" : "text-slate-400"}`}
// //                 />
// //               )}
// //               <div className="truncate">
// //                 <p className="leading-tight truncate">{builder.name}</p>
// //                 <p className="text-[10px] uppercase tracking-wider font-medium mt-0.5 text-slate-400">
// //                   {formatPeriodLabel(builder.periodType)}{" "}
// //                   {builder.isManager && "(Manager)"}
// //                 </p>
// //               </div>
// //             </button>
// //           ))}
// //         </nav>
// //       </aside>

// //       {/* ─── MAIN CONTENT AREA ─── */}
// //       <main className="flex-1 p-8 space-y-6 overflow-x-hidden max-w-6xl">
// //         {/* Header Toolbar */}
// //         <div className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-200 pb-5">
// //           <div>
// //             <h1 className="text-2xl font-bold tracking-tight text-slate-900">
// //               Builders Commission
// //             </h1>
// //             <p className="text-slate-500 text-sm">
// //               {selectedBuilder?.name || "Builder Summary"} • {periodLabel}
// //             </p>
// //           </div>

// //           <div className="flex items-center gap-3 print:hide">
// //             <input
// //               type="number"
// //               min="2000"
// //               max="2100"
// //               value={year}
// //               onChange={(e) => setYear(Number(e.target.value))}
// //               className="border border-slate-200 bg-white rounded-lg px-2.5 py-1.5 w-20 text-sm font-semibold text-center focus:outline-none focus:ring-1 focus:ring-slate-400"
// //             />

// //             <select
// //               value={period}
// //               onChange={(e) => setPeriod(e.target.value)}
// //               className="border border-slate-200 bg-white rounded-lg px-3 py-1.5 text-sm font-medium focus:outline-none focus:ring-1 focus:ring-slate-400"
// //             >
// //               <optgroup label="Global Options">
// //                 <option value="yearly">Full Year</option>
// //                 <option value="ytd">YTD Accumulation</option>
// //               </optgroup>

// //               {selectedBuilder?.periodType === "monthly" && (
// //                 <optgroup label="Months">
// //                   {[
// //                     "January",
// //                     "February",
// //                     "March",
// //                     "April",
// //                     "May",
// //                     "June",
// //                     "July",
// //                     "August",
// //                     "September",
// //                     "October",
// //                     "November",
// //                     "December",
// //                   ].map((m, i) => (
// //                     <option key={i} value={`month-${i + 1}`}>
// //                       {m}
// //                     </option>
// //                   ))}
// //                 </optgroup>
// //               )}

// //               {selectedBuilder?.periodType === "quarterly" && (
// //                 <optgroup label="Quarters">
// //                   <option value="quarter-q1">Q1 Block</option>
// //                   <option value="quarter-q2">Q2 Block</option>
// //                   <option value="quarter-q3">Q3 Block</option>
// //                   <option value="quarter-q4">Q4 Block</option>
// //                 </optgroup>
// //               )}

// //               {(selectedBuilder?.periodType === "semi_annual" ||
// //                 selectedBuilder?.periodType === "semi-annually" ||
// //                 selectedBuilder?.periodType === "semi-annual") && (
// //                 <optgroup label="Half-Year Cycles">
// //                   <option value="h1">First Half (H1)</option>
// //                   <option value="h2">Second Half (H2)</option>
// //                 </optgroup>
// //               )}
// //             </select>

// //             <Link
// //               to="/builders/setting"
// //               className="flex items-center gap-2 px-3.5 py-2 border border-slate-200 bg-white hover:border-indigo-200 hover:bg-indigo-50/50 active:scale-[0.98] text-slate-600 hover:text-indigo-600 rounded-lg text-sm font-semibold shadow-sm transition-all duration-150 ease-in-out select-none print:hide"
// //               title="Builder Settings"
// //             >
// //               <Settings className="w-4 h-4 shrink-0" />
// //               <span>Settings</span>
// //             </Link>

// //             {/* Added: Export Action Trigger Component */}
// //             <button
// //               onClick={exportAllToExcel}
// //               disabled={exporting || !commissionData}
// //               className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 active:scale-[0.98] disabled:bg-slate-100 disabled:text-slate-400 disabled:scale-100 text-white rounded-lg text-sm font-semibold shadow-sm transition-all duration-150 ease-in-out cursor-pointer select-none"
// //             >
// //               {exporting ? (
// //                 <RefreshCw className="w-4 h-4 text-emerald-200 animate-spin shrink-0" />
// //               ) : (
// //                 <Download className="w-4 h-4 text-white shrink-0" />
// //               )}
// //               <span>
// //                 {exporting ? "Exporting Ledger..." : "Export Current Tab"}
// //               </span>
// //             </button>
// //           </div>
// //         </div>

// //         {/* Display Workspace */}
// //         {loading ? (
// //           <div className="py-24 text-center">
// //             <RefreshCw className="w-6 h-6 animate-spin mx-auto text-slate-300 mb-2" />
// //             <p className="text-slate-400 text-xs uppercase tracking-wider font-semibold">
// //               Calculating Data...
// //             </p>
// //           </div>
// //         ) : commissionData ? (
// //           <div className="space-y-6">
// //             {/* Cards */}
// //             <div
// //               className={`grid grid-cols-1 ${commissionData.isManager ? "md:grid-cols-4" : "md:grid-cols-3"} gap-6`}
// //             >
// //               <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
// //                 <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
// //                   Total Sales
// //                 </p>
// //                 <p className="text-2xl font-bold text-slate-900 tracking-tight mt-1">
// //                   {formatCurrency(commissionData.totalSales)}
// //                 </p>
// //               </div>

// //               {commissionData.isManager && (
// //                 <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
// //                   <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
// //                     Personal Commission
// //                   </p>
// //                   <p className="text-2xl font-bold text-slate-900 tracking-tight mt-1">
// //                     {formatCurrency(commissionData.personalCommission)}
// //                   </p>
// //                 </div>
// //               )}

// //               {commissionData.isManager && (
// //                 <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm flex flex-col justify-between">
// //                   <div>
// //                     <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
// //                       Team Commission
// //                     </p>
// //                     <p className="text-2xl font-bold text-slate-900 tracking-tight mt-1">
// //                       {formatCurrency(commissionData.teamCommissionEarned)}
// //                     </p>
// //                     <p className="text-[11px] text-slate-400 mt-1 font-medium">
// //                       Rate:{" "}
// //                       {commissionData.calculationDetails?.managerTeamCommissions
// //                         ?.managerTeamRate || "0%"}{" "}
// //                       • Team Total Vol (Inc. Self):{" "}
// //                       {formatCurrency(
// //                         commissionData.calculationDetails
// //                           ?.managerTeamCommissions?.teamSalesTotal,
// //                       )}
// //                     </p>
// //                   </div>
// //                   <button
// //                     type="button"
// //                     onClick={() => setShowModalBreakdown(true)}
// //                     className="mt-3 text-left w-max text-xs font-bold text-indigo-600 hover:text-indigo-800 bg-transparent border-none p-0 cursor-pointer outline-none transition select-none print:hide"
// //                   >
// //                     View Details &rarr;
// //                   </button>
// //                 </div>
// //               )}

// //               <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
// //                 <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
// //                   {commissionData.isManager ? "Total Commission" : "Commission"}
// //                 </p>
// //                 <p className="text-2xl font-bold text-slate-900 tracking-tight mt-1">
// //                   {formatCurrency(commissionData.calculatedCommission)}
// //                 </p>
// //               </div>
// //             </div>

// //             {/* Matrix Table */}
// //             <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
// //               <div className="border-b border-slate-100 px-6 py-4">
// //                 <h3 className="text-sm font-semibold text-slate-800">
// //                   Rate Allocation Matrix
// //                 </h3>
// //               </div>

// //               <div className="overflow-x-auto">
// //                 <table className="w-full text-left border-collapse">
// //                   <thead>
// //                     <tr className="bg-slate-50/70 text-[11px] font-bold uppercase tracking-wider text-slate-400 border-b border-slate-100">
// //                       <th className="py-3 px-6">Tier Level</th>
// //                       <th className="py-3 px-6">Sales Volume Window</th>
// //                       <th className="py-3 px-6 text-right">Commission Rate</th>
// //                     </tr>
// //                   </thead>
// //                   <tbody className="divide-y divide-slate-100 text-sm">
// //                     {selectedBuilder?.brackets?.map((bracket, index) => {
// //                       const isCurrent =
// //                         commissionData.totalSales >= bracket.from &&
// //                         commissionData.totalSales <= bracket.to;

// //                       return (
// //                         <tr
// //                           key={index}
// //                           className={`transition ${isCurrent ? "bg-indigo-50/30 font-medium" : "text-slate-600"}`}
// //                         >
// //                           <td className="py-4 px-6 relative">
// //                             {isCurrent && (
// //                               <div className="absolute left-0 top-0 bottom-0 w-1 bg-indigo-600" />
// //                             )}
// //                             <span
// //                               className={
// //                                 isCurrent
// //                                   ? "text-indigo-600 font-semibold"
// //                                   : "text-slate-900"
// //                               }
// //                             >
// //                               Tier {index + 1}
// //                             </span>
// //                           </td>
// //                           <td className="py-4 px-6">
// //                             <div className="flex items-center gap-2 text-slate-500">
// //                               <span className="text-slate-900 font-mono">
// //                                 {formatCurrency(bracket.from)}
// //                               </span>
// //                               <ArrowRight className="w-3.5 h-3.5 opacity-50" />
// //                               <span className="font-mono">
// //                                 {bracket.to >= 999999999
// //                                   ? "Unlimited"
// //                                   : formatCurrency(bracket.to)}
// //                               </span>
// //                             </div>
// //                           </td>
// //                           <td className="py-4 px-6 text-right">
// //                             <span
// //                               className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${isCurrent ? "bg-indigo-100 text-indigo-800" : "bg-slate-100 text-slate-700"}`}
// //                             >
// //                               {Number(bracket.rate).toFixed(2)}%{" "}
// //                             </span>
// //                           </td>
// //                         </tr>
// //                       );
// //                     })}
// //                   </tbody>
// //                 </table>
// //               </div>
// //             </div>

// //             {/* ─── MODAL BREAKDOWN OVERLAY DIALOG ─── */}
// //             {showModalBreakdown &&
// //               commissionData.isManager &&
// //               commissionData.calculationDetails?.managerTeamCommissions
// //                 ?.rosterBreakdown && (
// //                 <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm animate-fadeIn print:hide">
// //                   <div className="bg-white border border-slate-200 rounded-2xl shadow-xl w-full max-w-3xl overflow-hidden flex flex-col max-h-[85vh]">
// //                     <div className="flex justify-between items-center border-b border-slate-100 px-6 py-4 sticky top-0 bg-white z-10">
// //                       <h3 className="text-sm font-bold text-slate-800">
// //                         Team Sales Volume Breakdown — {periodLabel}
// //                       </h3>
// //                       <button
// //                         type="button"
// //                         onClick={() => setShowModalBreakdown(false)}
// //                         className="text-slate-400 hover:text-slate-600 p-1.5 hover:bg-slate-50 rounded-lg transition cursor-pointer border-none bg-transparent"
// //                       >
// //                         <X className="w-4 h-4" />
// //                       </button>
// //                     </div>

// //                     <div className="overflow-y-auto flex-1 p-2">
// //                       <table className="w-full text-left border-collapse">
// //                         <thead>
// //                           <tr className="bg-slate-50/70 text-[10px] font-bold uppercase tracking-wider text-slate-400 border-b border-slate-100">
// //                             <th className="py-2.5 px-4 w-28">Salesman ID</th>
// //                             <th className="py-2.5 px-4">
// //                               Roster Contractor Name
// //                             </th>
// //                             <th className="py-2.5 px-4 text-right">
// //                               Period Sales Volume
// //                             </th>
// //                           </tr>
// //                         </thead>
// //                         <tbody className="divide-y divide-slate-100 text-sm text-slate-600">
// //                           {commissionData.calculationDetails.managerTeamCommissions.rosterBreakdown.map(
// //                             (member, mIdx) => (
// //                               <tr
// //                                 key={mIdx}
// //                                 className={
// //                                   member.isManagerSelf
// //                                     ? "bg-blue-50/20 font-medium"
// //                                     : ""
// //                                 }
// //                               >
// //                                 <td className="py-3 px-4 font-mono font-bold text-slate-400">
// //                                   {member.salesmanId}
// //                                 </td>
// //                                 <td className="py-3 px-4 font-bold text-slate-800">
// //                                   {member.name}{" "}
// //                                   {member.isManagerSelf && (
// //                                     <span className="text-[9px] ml-1 px-1 py-0.2 text-blue-600 bg-blue-100 rounded font-extrabold uppercase">
// //                                       Self
// //                                     </span>
// //                                   )}
// //                                 </td>
// //                                 <td className="py-3 px-4 text-right font-mono text-slate-900 font-semibold">
// //                                   {formatCurrency(member.salesVolume)}
// //                                 </td>
// //                               </tr>
// //                             ),
// //                           )}
// //                         </tbody>
// //                       </table>
// //                     </div>

// //                     <div className="flex justify-end px-6 py-3 border-t border-slate-100 bg-slate-50 rounded-b-2xl">
// //                       <button
// //                         type="button"
// //                         onClick={() => setShowModalBreakdown(false)}
// //                         className="px-4 py-1.5 bg-slate-900 hover:bg-slate-800 text-white rounded-lg font-bold text-xs shadow-sm transition cursor-pointer outline-none border-none"
// //                       >
// //                         Close Roster view
// //                       </button>
// //                     </div>
// //                   </div>
// //                 </div>
// //               )}
// //           </div>
// //         ) : (
// //           <div className="bg-white border border-slate-200 rounded-xl p-16 text-center shadow-sm text-slate-400">
// //             <Layers className="w-6 h-6 mx-auto mb-2 opacity-40 text-slate-900" />
// //             <p className="text-sm font-medium text-slate-600">
// //               No report metrics loaded.
// //             </p>
// //             <p className="text-slate-400 text-xs mt-0.5">
// //               Please check parameter inputs and sync parameters.
// //             </p>
// //           </div>
// //         )}
// //       </main>
// //     </div>
// //   );
// // };

// // export default BuildersCommission;
